import { Color, colorPool } from "./../../math/color.ts";
import { Matrix3d } from "../../math/matrix3d.ts";
import { Bounds } from "../../physics/bounds.ts";
import { releaseShadowQuads } from "../../renderable/groundshadow.js";
import {
	CANVAS_ONRESIZE,
	emit,
	GAME_RESET,
	ONCONTEXT_LOST,
	ONCONTEXT_RESTORED,
	off,
	on,
	RENDER_TARGET_CHANGED,
} from "../../system/event.ts";
import {
	blendStateFor,
	isAdvancedBlendMode,
	isSupportedBlendMode,
	normalizeBlendMode,
} from "../blendmodes.js";
import BlendEffect from "../effects/blendEffect.js";
import RadialGradientEffect from "../effects/radialGradient.js";
import { Gradient } from "../gradient.js";
import Renderer from "./../renderer.js";
import RenderTargetPool from "../rendertarget/render_target_pool.js";
import WebGLRenderTarget from "../rendertarget/webglrendertarget.js";
import { createAtlas, TextureAtlas } from "./../texture/atlas.js";
import TextureCache from "./../texture/cache.js";
import { FrameTexture } from "./../texture/frametexture.js";
import { dashPath, dashSegments } from "../utils/dash.js";
import {
	generateJoinCircles,
	generateTriangleFan,
} from "../utils/tessellation.js";
import { WebGLBatcher } from "./batchers/batcher.js";
import LitMeshBatcher from "./batchers/lit_mesh_batcher";
import LitQuadBatcher from "./batchers/lit_quad_batcher";
import MeshBatcher from "./batchers/mesh_batcher";
import PrimitiveBatcher from "./batchers/primitive_batcher";
import QuadBatcher from "./batchers/quad_batcher";
import { createLightUniformScratch, packLights } from "./lighting/pack.ts";
import OrthogonalTMXLayerGPURenderer from "./renderers/tmxlayer/orthogonal.js";
import { WebGLTextureStore } from "./texture/store.js";
import { resolveMaxTextures } from "./utils/maxtextures.js";
import { getMaxShaderPrecision } from "./utils/precision.js";
import { GLSamplerCache } from "./utils/samplercache.js";

/**
 * additional import for TypeScript
 * @import {Rect} from "./../../geometries/rectangle.ts";
 * @import {RoundRect} from "./../../geometries/roundrect.ts";
 * @import {Polygon} from "../../geometries/polygon.ts";
 * @import {Line} from "./../../geometries/line.ts";
 * @import {Ellipse} from "./../../geometries/ellipse.ts";
 * @import {Matrix2d} from "../../math/matrix2d.ts";
 * @import {Matrix3d} from "../../math/matrix3d.ts";
 * @import {default as Texture2d} from "../texture/texture2d.ts";
 */

// reusable constants for 2D→3D matrix operations
const _tempMatrix = new Matrix3d();

// pre-allocated matrices for blitEffect (avoids per-frame allocation)
const _savedTransform = new Matrix3d();
const _savedProjection = new Matrix3d();

/**
 * Backend-neutral blend tokens (see `video/blendmodes.js`, which holds the
 * one table both GPU backends share) translated to `gl.*` enums. Filled
 * lazily from a live context on the first `setBlendMode`, because the enum
 * values are properties of the context object rather than module constants.
 * @ignore
 * @internal
 */
let GL_BLEND_OP;
/**
 * @ignore
 * @internal
 */
let GL_BLEND_FACTOR;

/**
 * Build the neutral-token → `gl.*` enum lookups for this context.
 * @param {WebGL2RenderingContext} gl - a live context
 * @ignore
 * @internal
 */
function initBlendEnums(gl) {
	GL_BLEND_OP = {
		add: gl.FUNC_ADD,
		subtract: gl.FUNC_SUBTRACT,
		"reverse-subtract": gl.FUNC_REVERSE_SUBTRACT,
		min: gl.MIN,
		max: gl.MAX,
	};
	GL_BLEND_FACTOR = {
		zero: gl.ZERO,
		one: gl.ONE,
		src: gl.SRC_COLOR,
		"one-minus-src": gl.ONE_MINUS_SRC_COLOR,
		"src-alpha": gl.SRC_ALPHA,
		"one-minus-src-alpha": gl.ONE_MINUS_SRC_ALPHA,
		dst: gl.DST_COLOR,
		"one-minus-dst": gl.ONE_MINUS_DST_COLOR,
		"dst-alpha": gl.DST_ALPHA,
		"one-minus-dst-alpha": gl.ONE_MINUS_DST_ALPHA,
	};
}

/**
 * a WebGL renderer object
 * @category Rendering
 */
export default class WebGLRenderer extends Renderer {
	/**
	 * @param {ApplicationSettings} [options] - optional parameters for the renderer
	 */
	constructor(options) {
		// parent contructor
		super(Object.assign(options, { context: "webgl" }));

		/**
		 * The vendor string of the underlying graphics driver.
		 * @type {string}
		 * @default undefined
		 * @readonly
		 */
		this.GPUVendor = undefined;

		/**
		 * The renderer string of the underlying graphics driver.
		 * @type {string}
		 * @default undefined
		 * @readonly
		 */
		this.GPURenderer = undefined;

		/**
		 * The WebGL context
		 * @type {WebGLRenderingContext}
		 */
		this.gl = this.renderTarget.context;

		/**
		 * cached FBO for post-effect processing (lazily created on first use)
		 * @type {WebGLRenderTarget|null}
		 * @ignore
		 * @internal
		 */
		// initialize the render target pool with a WebGL factory
		// capture targets rasterize the scene — under antiAlias they get a
		// multisampled render half resolved via blitFramebuffer, so MSAA
		// composes with post-effects instead of silently turning off the
		// moment a camera has one. Ping-pong intermediates stay 1×.
		const msaaSamples =
			this.settings.antiAlias === true
				? Math.min(4, this.gl.getParameter(this.gl.MAX_SAMPLES))
				: 0;
		/**
		 * @ignore
		 * @internal
		 */
		this._renderTargetPool = new RenderTargetPool((w, h, isCapture) => {
			return new WebGLRenderTarget(this.gl, w, h, {
				samples: isCapture === true ? msaaSamples : 0,
			});
		});

		/**
		 * Saved projection matrix for begin/endPostEffect.
		 * @type {Matrix3d}
		 * @ignore
		 * @internal
		 */
		// projections saved by beginPostEffect, one per (possibly nested)
		// active pass — preallocated slots + a depth counter (zero-alloc
		// steady state), matching RenderTargetPool's base stack
		/**
		 * @ignore
		 * @internal
		 */
		this._effectProjectionStack = [];
		/**
		 * @ignore
		 * @internal
		 */
		this._effectPassDepth = 0;

		/**
		 * Advanced-blend state. The six CSS modes fixed-function cannot
		 * express are composited by capturing the destination, rendering the
		 * draw to an offscreen target, and blitting that through
		 * {@link BlendEffect}. All of it is created lazily — a game that
		 * never asks for an advanced mode pays nothing.
		 *
		 * `_advancedBlendBusy` is a DEPTH, not a flag: the bracket nests
		 * inside itself (the composite blit re-enters `setBatcher`, and
		 * `toFrameTexture` does too), so a boolean would be cleared by the
		 * inner scope while the outer one is still running.
		 * @ignore
		 * @internal
		 */
		this._advancedBlendEffect = undefined;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendCapture = undefined;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendTarget = undefined;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendOpen = false;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendBusy = 0;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendParent = null;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendStencil = false;
		/**
		 * @ignore
		 * @internal
		 */
		this._advancedBlendWarned = new Set();

		/**
		 * sets or returns the thickness of lines for shape drawing
		 * @type {number}
		 * @default 1
		 */
		this.lineWidth = 1;

		/**
		 * sets or returns the shape used to join two line segments where they meet.
		 * Out of the three possible values for this property: "round", "bevel", and "miter", only "round" is supported for now in WebGL
		 * @type {string}
		 * @default "round"
		 */
		this.lineJoin = "round";

		/**
		 * the vertex buffer used by this WebGL Renderer
		 * @type {WebGLBuffer}
		 */
		this.vertexBuffer = this.gl.createBuffer();

		/**
		 * Sampler objects, deduplicated by state. GL bakes wrap/filter into the
		 * texture object; a bound sampler overrides that, so one texture can
		 * serve several variants at once — which is what lets residency be
		 * keyed by source alone. Renderer-owned so every batcher shares it.
		 * @type {GLSamplerCache}
		 * @ignore
		 * @internal
		 */
		this.samplerCache = new GLSamplerCache(this.gl);

		/**
		 * Texture residency, keyed by SOURCE. The GL handle used to be
		 * reachable only through a batcher's per-unit array, so dropping a unit
		 * assignment destroyed the texture and the next draw rebuilt it from
		 * scratch — a full re-upload and mip regeneration per quad once past
		 * the batching limit. Owning it here, per renderer, means a texture
		 * that merely moves units costs a bind.
		 *
		 * Renderer-owned rather than batcher-owned on purpose: `init()` re-runs
		 * on context restore, so a batcher-owned store would be REPLACED there,
		 * orphaning every handle it tracked and leaking them on the next loss.
		 * @type {TextureStore}
		 * @ignore
		 * @internal
		 */
		this.textureStore = new WebGLTextureStore(this.gl);

		/**
		 * Maximum number of texture unit supported under the current context
		 * @type {number}
		 * @readonly
		 */
		this.maxTextures = resolveMaxTextures(
			this.gl.getParameter(this.gl.MAX_TEXTURE_IMAGE_UNITS),
			this.settings.maxTextures,
		);
		/**
		 * Next free indexed `UNIFORM_BUFFER` binding point, handed out by
		 * {@link WebGLRenderer#reserveUniformBindingPoint}. Binding points are
		 * a context-wide namespace shared by every uniform block, so they are
		 * allocated here rather than picked as literals by whoever needs one —
		 * the same reason texture units go through `TextureCache.reserveUnit`.
		 * @type {number}
		 * @ignore
		 * @internal
		 */
		this._nextUniformBindingPoint = 0;

		/**
		 * the default shader precision based on application settings
		 * @type {string}
		 * @ignore
		 * @internal
		 */
		this.shaderPrecision = getMaxShaderPrecision(
			this.gl,
			this.settings.highPrecisionShader !== false,
		);

		/**
		 * reusable scratch array for fillRect (2 triangles = 6 vertices)
		 * @ignore
		 * @internal
		 */
		this._rectTriangles = Array.from({ length: 6 }, () => {
			return { x: 0, y: 0 };
		});

		// scratch AABB reused by `clipRect` for screen-space corner
		// transforms, kept on the instance so the call doesn't allocate.
		/**
		 * @ignore
		 * @internal
		 */
		this._clipAABB = new Bounds();

		// scratch array for fillPolygon to avoid mutating polygon points
		/**
		 * @ignore
		 * @internal
		 */
		this._polyVerts = [];

		// current gradient state (null when using solid color)
		/**
		 * @ignore
		 * @internal
		 */
		this._currentGradient = null;

		// the stencil value of currently-VISIBLE pixels under the innermost
		// active mask, as installed by setMask (0 for an inverted mask,
		// maskLevel otherwise) — the single source #gradientMask gates and
		// restores against
		/**
		 * @ignore
		 * @internal
		 */
		this._maskVisibleRef = 0;

		/**
		 * The current transformation matrix used for transformations on the overall scene
		 * (alias to renderState.currentTransform for backward compatibility)
		 * @type {Matrix3d}
		 */
		this.currentTransform = this.renderState.currentTransform;

		/**
		 * The current batcher used by the renderer
		 * @type {WebGLBatcher}
		 */
		this.currentBatcher = undefined;

		// `currentProgram` is an accessor over the context (see the getter
		// below), so there is no field to initialise here — clearing it via
		// the setter keeps the record defined from the start.
		this.currentProgram = undefined;

		/**
		 * the GL texture unit currently active (`gl.activeTexture`) — global
		 * GL state, so tracked once per renderer and shared by every batcher
		 * through the `MaterialBatcher.currentTextureUnit` accessor. `-1`
		 * forces the next bind to re-issue `gl.activeTexture`.
		 * @type {number}
		 * @ignore
		 * @internal
		 */
		this._activeTextureUnit = -1;

		/**
		 * The list of active batchers
		 * @type {Map<WebGLBatcher>}
		 */
		this.batchers = new Map();

		// (no raw vertex-buffer bind here — each batcher's vertex state
		// captures its buffers at init, and bind() binds the upload target)

		// Create the default batchers. The lit-aware quad batcher is only
		// created when no custom batcher override is supplied — its lit
		// fragment path needs the paired (color, normal) sampler layout.
		const CustomBatcher = this.settings.batcher || this.settings.compositor;
		this.addBatcher(new (CustomBatcher || QuadBatcher)(this), "quad", true);
		if (!CustomBatcher) {
			this.addBatcher(new LitQuadBatcher(this), "litQuad");
		}
		this.addBatcher(new (CustomBatcher || PrimitiveBatcher)(this), "primitive");
		this.addBatcher(new MeshBatcher(this), "mesh");
		this.addBatcher(new LitMeshBatcher(this), "litMesh");

		// default WebGL state(s)
		// depth testing disabled for 2D (painter's algorithm handles z-ordering).
		// `MeshBatcher.bind()` enables it temporarily for 3D mesh rendering.
		this.gl.disable(this.gl.DEPTH_TEST);
		this.gl.depthMask(false);

		this.gl.disable(this.gl.SCISSOR_TEST);
		/**
		 * @ignore
		 * @internal
		 */
		this._scissorActive = false;
		this.gl.enable(this.gl.BLEND);

		// set default mode
		this.setBlendMode(this.settings.blendMode);

		// get GPU vendor and renderer
		const debugInfo = this.gl.getExtension("WEBGL_debug_renderer_info");
		if (debugInfo !== null) {
			this.GPUVendor = this.gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
			this.GPURenderer = this.gl.getParameter(
				debugInfo.UNMASKED_RENDERER_WEBGL,
			);
		}

		// customShader is declared on the base Renderer class

		// Create a texture cache
		this.cache = new TextureCache(this, this.maxTextures);

		// set the renderer type
		this.type = "WebGL2";

		// this backend renders TMX tile layers on the GPU (see TMXLayer)
		this.supportsShaderTileLayers = true;

		// this backend keeps mesh geometry resident on the GPU, so meshes can
		// hand it their model matrix instead of pre-transformed vertices
		this.supportsRetainedMesh = true;

		// drawElementsInstanced is WebGL 2 core
		this.supportsInstancing = true;

		// GLSL, not "shaders exist" — `ShaderEffect` and the loader's
		// `{vertex, fragment}` assets hand their source straight to the
		// driver, so the language is the thing they have to agree on
		/** @type {"glsl"|"wgsl"|null} */
		this.shaderLanguage = "glsl";

		// depth buffer + 3D projection + drawMesh, i.e. everything a
		// depth-sorted Camera3d scene needs
		this.supportsDepthBuffer = true;

		// to simulate context lost and restore in WebGL:
		// let ctx = me.video.renderer.context.getExtension('WEBGL_lose_context');
		// ctx.loseContext()
		this.getCanvas().addEventListener(
			"webglcontextlost",
			(e) => {
				e.preventDefault();
				this.isContextValid = false;
				// driver auto-frees lost resources; just drop the ref
				this.vertexBuffer = null;
				emit(ONCONTEXT_LOST, this);
			},
			false,
		);
		this.getCanvas().addEventListener(
			"webglcontextrestored",
			() => {
				// restore renderer-owned GL state before downstream subscribers run
				// (no raw bind needed — reset() below re-inits every batcher,
				// rebuilding their vertex states against this new buffer)
				this.vertexBuffer = this.gl.createBuffer();

				// driver wipes all GL state on restore; re-apply ours
				this.gl.disable(this.gl.DEPTH_TEST);
				this.gl.depthMask(false);
				this.gl.disable(this.gl.SCISSOR_TEST);
				this._scissorActive = false;
				this.gl.enable(this.gl.BLEND);
				// invalidate the blend cache: blendFunc/blendEquation are back
				// at driver defaults, so a matching cached mode would otherwise
				// short-circuit setBlendMode and never re-apply them
				this.currentBlendMode = undefined;
				this.setBlendMode(this.settings.blendMode);

				// reset() re-inits batchers, FBO pool, light shader, TMX renderer
				this.reset();

				// stale per-source unit assignments — force re-upload on next draw
				this.cache.units.clear();
				this.cache.usedUnits.clear();
				// the samplers and textures died with the context; drop our side
				// so the next resolve mints fresh ones against the restored one.
				// CLEARED, never replaced: rebuilding these would orphan every
				// handle they tracked and leak it all on the next loss.
				this.samplerCache.releaseAll();
				this.textureStore.releaseAll();

				// the restored context is back at TEXTURE0 — invalidate the
				// shared active-unit tracking so the next bind re-issues it
				this._activeTextureUnit = -1;

				this.isContextValid = true;
				emit(ONCONTEXT_RESTORED, this);
			},
			false,
		);

		// Held as bound fields rather than inline arrows so `destroy()` can
		// actually unregister them. An anonymous handler cannot be passed to
		// `off()`, so every torn-down renderer used to leave its listeners on
		// the bus forever — and each closure pins the renderer, its batchers
		// and its GL objects against garbage collection, so releasing the GL
		// context alone was not enough to let the canvas go.
		this.onGameReset = () => {
			this.reset();
		};
		this.onContextRestoredInvalidate = (renderer) => {
			if (renderer === this) {
				this.currentProgram = undefined;
			}
		};
		this.onCanvasResize = (width, height) => {
			this.flush();
			this.setViewport(0, 0, width, height);
			// FBOs are lazily resized in beginPostEffect via get() → resize()
		};

		// reset the renderer on game reset
		on(GAME_RESET, this.onGameReset);

		// Every live GLShader recompiles on this event, and each recompile
		// binds its own program to replay its uniform snapshot — so the
		// REAL current program afterwards is whichever shader recompiled
		// last, while this cache still names whatever drew before the
		// event. Invalidate it (order-immune: whatever is really bound,
		// the next flush's syncProgram re-issues useProgram) instead of
		// trusting a stale match and drawing with a foreign program.
		// Latent for years; surfaced when the Light3dBlock outgrew the
		// Light2dBlock — a foreign mesh-lit program parks its block at
		// default binding point 0, whose 2D buffer is now too small, and a
		// plain sprite flush dies with INVALID_OPERATION.
		on(ONCONTEXT_RESTORED, this.onContextRestoredInvalidate);

		// register to the CANVAS resize channel
		on(CANVAS_ONRESIZE, this.onCanvasResize);
	}

	/**
	 * The WebGL version used by this renderer.
	 * @type {number}
	 * @default 2
	 * @deprecated since 20.0.0 — the WebGL renderer is WebGL 2 only, this is always `2`
	 */
	get WebGLVersion() {
		return 2;
	}

	/**
	 * return the list of supported compressed texture formats
	 * @return {Object}
	 */
	getSupportedCompressedTextureFormats() {
		// per-instance (the WebGPU backend's convention): a module-level memo
		// would be shared across coexisting renderer instances and survive a
		// destroyed context — divergence-by-drift, even if format support
		// rarely differs in practice
		if (typeof this._compressedTextureFormats === "undefined") {
			const gl = this.gl;
			if (typeof gl === "undefined" || gl === null) {
				// WebGL context not available
				return super.getSupportedCompressedTextureFormats();
			}
			/**
			 * @ignore
			 * @internal
			 */
			this._compressedTextureFormats = {
				astc:
					gl.getExtension("WEBGL_compressed_texture_astc") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_astc"),
				bptc:
					gl.getExtension("EXT_texture_compression_bptc") ||
					gl.getExtension("WEBKIT_EXT_texture_compression_bptc"),
				s3tc:
					gl.getExtension("WEBGL_compressed_texture_s3tc") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc"),
				s3tc_srgb:
					gl.getExtension("WEBGL_compressed_texture_s3tc_srgb") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc_srgb"),
				pvrtc:
					gl.getExtension("WEBGL_compressed_texture_pvrtc") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_pvrtc"),
				etc1:
					gl.getExtension("WEBGL_compressed_texture_etc1") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_etc1"),
				etc2:
					gl.getExtension("WEBGL_compressed_texture_etc") ||
					gl.getExtension("WEBKIT_WEBGL_compressed_texture_etc") ||
					gl.getExtension("WEBGL_compressed_texture_es3_0"),
			};
			// ETC2 is a superset of ETC1 — if we have ETC2 but not ETC1,
			// synthesize ETC1 support so that PKM/KTX ETC1 textures work
			if (
				!this._compressedTextureFormats.etc1 &&
				this._compressedTextureFormats.etc2
			) {
				this._compressedTextureFormats.etc1 = {
					COMPRESSED_RGB_ETC1_WEBGL: 0x8d64,
				};
			}
		}
		return this._compressedTextureFormats;
	}

	/**
	 * return true if the given compressed texture format is supported
	 * @param {Number} format
	 * @returns
	 */
	hasSupportedCompressedFormats(format) {
		const supportedFormats = this.getSupportedCompressedTextureFormats();
		for (const supportedFormat in supportedFormats) {
			for (const extension in supportedFormats[supportedFormat]) {
				if (format === supportedFormats[supportedFormat][extension]) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * Reset context state
	 */
	/**
	 * Tear down this renderer and free GPU/event resources. Walks every
	 * registered batcher's `destroy()` so cross-renderer subscriptions
	 * (`GPU_TEXTURE_CACHE_RESET` on `MaterialBatcher`, etc.) don't
	 * leak across `Application.destroy()` cycles. Safe to call multiple
	 * times — subsequent calls are no-ops.
	 */
	/**
	 * Release any GPU geometry retained for the given mesh. Called when a mesh
	 * is destroyed; harmless if it never had any.
	 * @param {object} mesh - the mesh whose geometry should be freed
	 */
	deleteMeshGeometry(mesh) {
		// a mesh torn down between being queued and the transparent pass
		// running must not be replayed — the replay would re-upload geometry
		// for something the caller has finished with
		this.removeQueuedTransparent(mesh);
		this.batchers?.forEach((batcher) => {
			batcher.releaseRetained?.(mesh);
		});
	}

	destroy() {
		// Unregister first: every handler closes over `this`, so leaving them
		// on the bus keeps the renderer (and transitively its batchers and GL
		// objects) reachable forever — a destroyed renderer would also still
		// react to GAME_RESET and CANVAS_ONRESIZE. Matches what the WebGPU
		// backend already does.
		off(GAME_RESET, this.onGameReset);
		off(ONCONTEXT_RESTORED, this.onContextRestoredInvalidate);
		off(CANVAS_ONRESIZE, this.onCanvasResize);

		// advanced-blend resources, all lazily created: a game that never used
		// one of the advanced modes has nothing here to release
		this._advancedBlendEffect?.destroy();
		this._advancedBlendEffect = undefined;
		this._advancedBlendCapture?.destroy();
		this._advancedBlendCapture = undefined;
		this._advancedBlendTarget?.destroy?.();
		this._advancedBlendTarget = undefined;
		this._advancedBlendOpen = false;
		this._advancedBlendBusy = 0;

		// the shared ground-shadow quads (#1515) hold retained GPU geometry
		// keyed off this renderer's batchers — released before those go
		releaseShadowQuads(this);
		if (this.batchers) {
			this.batchers.forEach((batcher) => {
				batcher.destroy?.();
			});
			this.batchers.clear();
		}
		// the shared vertex buffer is renderer-owned (batchers only
		// reference it), so it is released here rather than per batcher
		if (this.vertexBuffer) {
			this.gl.deleteBuffer(this.vertexBuffer);
			this.vertexBuffer = null;
		}

		// Release the GL context itself. Dropping every GL object above and
		// removing the canvas from the DOM does NOT do this: a canvas keeps
		// its context until the canvas is garbage-collected, which is
		// non-deterministic and routinely delayed. Browsers cap how many live
		// contexts they keep (Chromium ~16) and force-lose the oldest past
		// that, so a long-lived page that creates and tears down several
		// applications — an SPA moving between scenes, or a test session
		// sharing one page across spec files — accumulates dead-but-unfreed
		// contexts until an unrelated later `getContext` stalls or fails.
		//
		// `WEBGL_lose_context` is the only way to hand a context back
		// deterministically. `destroy()` is terminal (`Application.destroy`
		// refuses a subsequent `init`), so losing it here forecloses nothing.
		// The extension is absent on some drivers, hence the optional call.
		if (this.isContextValid !== false) {
			this.gl.getExtension("WEBGL_lose_context")?.loseContext();
		}
	}

	reset() {
		super.reset();

		// drop any pass state orphaned by a mid-pass exception — a stale
		// depth would misalign every later pass and inflate the pool's
		// nesting forever (the pool's own stack is cleared via its
		// destroy() below; the preallocated slots are kept for reuse)
		this._effectPassDepth = 0;

		// clear gl context
		this.clear();

		// drop every per-layer GPU tilemap texture — tile layers churn on
		// game reset and we'd otherwise leak a WebGLTexture per layer
		// across each level transition
		this._orthogonalTMXGPURenderer?.reset();

		// initial viewport size
		this.setViewport();

		this.currentBatcher = undefined;
		this.currentProgram = undefined;
		this.customShader = undefined;

		this.batchers.forEach((batcher) => {
			if (this.isContextValid === false) {
				// on context lost/restore
				batcher.init(this);
			} else {
				batcher.reset();
			}
		});

		this.setBatcher("quad");

		this.gl.disable(this.gl.SCISSOR_TEST);
		this._scissorActive = false;

		// release post-process FBOs (will be recreated on demand)
		this._renderTargetPool.destroy();

		// drop the lazily-cached drawLight resources — after a context
		// loss/restore the cached shader program and white-pixel atlas
		// reference the OLD GL context and would error if reused.
		// Lazy re-init happens on the next drawLight call.
		if (this._lightShader !== undefined) {
			this._lightShader.destroy?.();
			/**
			 * @ignore
			 * @internal
			 */
			this._lightShader = undefined;
		}
		if (this._lightAtlas !== undefined) {
			// `TextureCache` is keyed by the source image, not by the
			// `TextureAtlas` instance. Dropping the wrong key would leak
			// the entry (so a context lost / restore would later resolve
			// the canvas to a `TextureAtlas` whose internal GL texture
			// reference is invalid). Iterate the atlas's sources to drop
			// every cached entry it registered in `cache.set(source, this)`.
			this._lightAtlas.sources.forEach((source) => {
				this.cache.delete?.(source);
			});
			/**
			 * @ignore
			 * @internal
			 */
			this._lightAtlas = undefined;
		}

		// the shared frame-capture texture (toFrameTexture) holds a GL texture
		// tied to this context — drop it so the next capture reallocates. Safe
		// on a still-valid context too (a resize/reset just re-creates it).
		if (typeof this._frameTexture !== "undefined") {
			this._frameTexture.destroy();
			/**
			 * @ignore
			 * @internal
			 */
			this._frameTexture = undefined;
		}

		// Context-loss-only cleanup for the TMX GPU renderer: the cached
		// `GLShader` and per-layer GL textures reference the OLD context
		// and are invalid. On a regular `GAME_RESET` (context still
		// valid) we already dropped per-layer textures via `.reset()`
		// above and keep the renderer instance so its compiled shader
		// program survives across level transitions instead of leaking a
		// `WebGLProgram` per reset and re-paying the compile cost.
		if (this.isContextValid === false) {
			/**
			 * @ignore
			 * @internal
			 */
			this._orthogonalTMXGPURenderer = undefined;
		}
	}

	/**
	 * Draw a TMX tile layer through whichever path the layer's `renderMode`
	 * resolves to. WebGL2-eligible layers (`renderMode === "shader"`) take
	 * the procedural shader path — one quad per tileset, GID lookup in a
	 * per-layer data texture. All other layers fall through to the base
	 * `Renderer.drawTileLayer` (preRender blit or per-tile loop).
	 * @param {object} layer - the TMXLayer to draw
	 * @param {object} rect - the visible region in world coords
	 */
	drawTileLayer(layer, rect) {
		if (layer.renderMode === "shader") {
			const gpu = this._getTMXGPURendererFor(layer.orientation);
			if (gpu !== undefined) {
				gpu.draw(layer, rect);
				return;
			}
		}
		super.drawTileLayer(layer, rect);
	}

	/**
	 * Lazy-init the orientation-specific GPU tilemap renderer.
	 * @param {string} orientation
	 * @returns {object|undefined}
	 * @ignore
	 * @internal
	 */
	_getTMXGPURendererFor(orientation) {
		if (orientation === "orthogonal") {
			if (this._orthogonalTMXGPURenderer === undefined) {
				try {
					this._orthogonalTMXGPURenderer = new OrthogonalTMXLayerGPURenderer(
						this,
					);
				} catch (err) {
					// shader compile / link failure on this driver — disable
					// the GPU path permanently for the rest of the session
					// (the next `drawTileLayer` call falls through to the
					// legacy renderer). Stored as `null` so subsequent
					// attempts short-circuit without re-trying the failing
					// compile every frame.
					console.warn(
						"melonJS: GPU tilemap shader failed to compile, falling back to legacy renderer",
						err,
					);
					this._orthogonalTMXGPURenderer = null;
				}
			}
			return this._orthogonalTMXGPURenderer || undefined;
		}
		// isometric / staggered / hexagonal: phase 2
		return undefined;
	}

	/**
	 * add a new batcher to this renderer
	 * @param {WebGLBatcher} batcher - a batcher instance (must extend WebGLBatcher)
	 * @param {string} name - a name uniquely identifying this batcher
	 * @param {boolean} [activate=false] - true if the given batcher should be set as the active one
	 */
	addBatcher(batcher, name = "default", activate = false) {
		// gate on the BACKEND base, not the neutral one: a neutral or
		// WebGPU batcher has none of the GL machinery (defaultShader,
		// setProjection, …) and would fail mid-activation instead
		if (!(batcher instanceof WebGLBatcher)) {
			throw new Error(
				"addBatcher: batcher must be a WebGLBatcher subclass (custom WebGL batchers extend WebGLBatcher)",
			);
		}
		if (typeof this.batchers.get(name) !== "undefined") {
			throw new Error("Invalid Batcher name");
		}

		this.batchers.set(name, batcher);

		if (activate === true) {
			this.setBatcher(name);
		}
	}

	/**
	 * set the active batcher for this renderer
	 * @param {string} name - a batcher name
	 * @param {GLShader} [shader] - an optional shader program to be used, instead of the default one, when activating the batcher
	 * @returns {WebGLBatcher} an instance to the current active batcher
	 */
	setBatcher(name = "default", shader) {
		const batcher = this.batchers.get(name);

		if (typeof batcher === "undefined") {
			throw new Error("Invalid Batcher");
		}

		// Advanced blend modes bracket EACH draw. Every draw entry point on
		// this renderer calls setBatcher, which makes this the one place that
		// sees them all — but it must sit ABOVE the same-batcher/same-shader
		// fast path below, or the bracket would follow batcher SWITCHES
		// rather than draws and the per-draw semantics would collapse.
		if (this._advancedBlendBusy === 0) {
			if (this._advancedBlendOpen === true) {
				this._closeAdvancedBlend();
			}
			if (isAdvancedBlendMode(this.currentBlendMode) === true) {
				if (name === "mesh" || name === "litMesh") {
					// the offscreen gets a fresh depth buffer and the composite
					// is a screen-space quad writing no depth, so later meshes
					// would not test against it
					this._warnAdvancedBlendFallback("3D meshes");
				} else {
					this._openAdvancedBlend();
				}
			}
		}

		// resolve the target shader — the explicitly-passed one if any,
		// otherwise the batcher's default. We always reconcile the
		// currentShader to this target so a custom shader left bound by a
		// prior call (e.g. `drawLight` parking the radial-gradient
		// program) gets evicted before the next sprite batch flushes.
		// `shader != null` excludes both `null` and `undefined`
		// (`typeof null === "object"` would otherwise let null through).
		const targetShader = shader != null ? shader : batcher.defaultShader;

		if (
			this.currentBatcher === batcher &&
			batcher.currentShader === targetShader
		) {
			// fast path: same batcher, same shader — nothing to do.
			return this.currentBatcher;
		}

		if (this.currentBatcher !== batcher) {
			if (this.currentBatcher !== undefined) {
				// flush the current batcher, then let it tear down any
				// state it set up at `bind()` time (Mesh batcher restores
				// non-mesh blend/depth defaults here, for example)
				this.currentBatcher.flush();
				this.currentBatcher.unbind();
			}
			this.currentBatcher = batcher;
			// `bind()` is where each batcher sets up its own GL state
			// (vertex attributes, plus mode-specific state like depth /
			// blend for `MeshBatcher`). Renderer doesn't need to know.
			this.currentBatcher.bind();
			// sync the projection matrix to the new batcher
			this.currentBatcher.setProjection(this.projectionMatrix);
		}

		// useShader() is internally a no-op when the shader is already
		// bound; it flushes and rebinds otherwise. Pending vertices that
		// were queued under the prior shader get drained against that
		// shader before the switch, which is exactly what we want.
		this.currentBatcher.useShader(targetShader);

		return this.currentBatcher;
	}

	/**
	 * Reset the gl transform to identity
	 */
	resetTransform() {
		this.currentTransform.identity();
	}

	/**
	 * Create a pattern with the specified repetition
	 * @param {HTMLImageElement|SVGImageElement|HTMLVideoElement|HTMLCanvasElement|ImageBitmap|OffscreenCanvas|VideoFrame} image - Source image to be used as the pattern's image
	 * @param {string} [repeat="no-repeat"] - Define how the pattern should be repeated. One of `"repeat"` / `"repeat-x"` / `"repeat-y"` / `"no-repeat"`.
	 * @returns {TextureAtlas} the patterned texture created
	 * @see ImageLayer#repeat
	 * @example
	 * let tileable   = renderer.createPattern(image, "repeat");
	 * let horizontal = renderer.createPattern(image, "repeat-x");
	 * let vertical   = renderer.createPattern(image, "repeat-y");
	 * let basic      = renderer.createPattern(image, "no-repeat");
	 */
	createPattern(image, repeat = "no-repeat") {
		this.setBatcher("quad");

		// No band-aid texture cleanup here anymore — the cache's
		// `(source, repeat)` unit keying (see `TextureCache.getUnit` /
		// `peekUnit` / `freeTextureUnit`, which inline the `(source,
		// repeat)` lookup) already separates this call from any
		// previous createPattern over the same image with a different
		// repeat mode. A repeat match against an existing call reuses
		// the same unit, and `uploadTexture`'s `boundTextures[unit]`
		// check short-circuits a redundant GL upload. Fixes #1448 (the
		// prior delete-and-replace trampled the still-live pattern
		// returned by the earlier call).
		const texture = new TextureAtlas(
			createAtlas(image.width, image.height, "pattern", repeat),
			image,
		);

		this.currentBatcher.uploadTexture(texture);

		return texture;
	}

	/**
	 * Flush the batcher to the frame buffer
	 */
	flush() {
		// A flush while an advanced-blend bracket is open means "put this on
		// screen now" — which for a bracketed draw means closing it, since
		// its geometry is sitting in an offscreen target that has not been
		// composited yet. This is what makes the LAST draw of a frame land:
		// nothing calls setBatcher after it.
		if (this._advancedBlendOpen === true && this._advancedBlendBusy === 0) {
			this._closeAdvancedBlend();
			return;
		}
		this.currentBatcher.flush();
	}

	/**
	 * Upload per-frame Light2d uniforms used by the lit sprite pipeline.
	 *
	 * Packs the active lights into pre-allocated scratch buffers, then
	 * forwards to `LitQuadBatcher`. Light positions are translated from
	 * world-space (where `light.getBounds().centerX/Y` lives) into the
	 * renderer's pre-projection coords by subtracting `(translateX, translateY)`,
	 * matching what `Stage.drawLighting` does for the cutout pass — so
	 * the lit fragment's `lightPos - vWorldPos` math lines up with the
	 * camera's view.
	 *
	 * Lights past `MAX_LIGHTS` are silently dropped. Also caches the
	 * active light count on the renderer so `drawImage` can dispatch
	 * normal-mapped sprites to the lit batcher only when there's
	 * something to light them with.
	 * @param {Iterable<object>} [lights] - active `Light2d` instances; falsy/empty no-ops the lit pipeline
	 * @param {object} [ambient] - ambient lighting color (0..255 RGB); defaults to black
	 * @param {number} [translateX=0] - world-to-screen X translate (matches `Camera2d.draw()`)
	 * @param {number} [translateY=0] - world-to-screen Y translate
	 */
	setLightUniforms(lights, ambient, translateX = 0, translateY = 0) {
		// scratch is allocated lazily on first call so non-lit scenes
		// don't pay for it
		if (this._lightUniformsScratch === undefined) {
			/**
			 * @ignore
			 * @internal
			 */
			this._lightUniformsScratch = createLightUniformScratch();
		}
		const u = packLights(
			lights,
			ambient,
			translateX,
			translateY,
			this._lightUniformsScratch,
		);

		this.activeLightCount = u.count;
		const lit = this.batchers.get("litQuad");
		if (lit && typeof lit.setLightUniforms === "function") {
			// A uniform-buffer upload does not touch `useProgram`, so this no
			// longer disturbs the active shader. It used to: the light data
			// went through `GLShader.setUniform`, which binds litQuad's program
			// to write, and litQuad is rarely the *active* batcher — leaving it
			// bound fed 4-attribute vertex data to a 5-attribute shader.
			lit.setLightUniforms(u);
		}
	}

	/**
	 * Claim the next indexed `UNIFORM_BUFFER` binding point.
	 *
	 * Binding points are context-wide: two uniform blocks sharing one silently
	 * overwrite each other, and the loser reads the winner's bytes as its own.
	 * Allocating them from here rather than hard-coding an integer per feature
	 * makes that collision impossible, and mirrors how texture units are
	 * claimed through {@link TextureCache#reserveUnit}.
	 *
	 * Callers hold their point for the life of the batcher — a context restore
	 * re-runs `init()`, which must reuse the point it already has rather than
	 * claim another, or a long-lived session would leak through the budget.
	 * @returns {number} the claimed binding point
	 * @throws {Error} when the device's binding points are exhausted
	 * @ignore
	 * @internal
	 */
	reserveUniformBindingPoint() {
		const max = this.gl.getParameter(this.gl.MAX_UNIFORM_BUFFER_BINDINGS);
		if (this._nextUniformBindingPoint >= max) {
			throw new Error(
				`WebGLRenderer: no free uniform buffer binding point (device limit ${max})`,
			);
		}
		return this._nextUniformBindingPoint++;
	}

	/**
	 * @inheritdoc
	 *
	 * Renders the light as a quad through a shared
	 * {@link RadialGradientEffect} fragment shader (procedural — no
	 * per-light texture). The shader and a shared 1×1 white-pixel atlas
	 * are lazy-allocated on first call and reused for every Light2d on
	 * this renderer. Each light's color and intensity are encoded into
	 * the per-vertex tint so consecutive `drawLight` calls accumulate
	 * into the quad batcher's buffer and flush together — N lights
	 * become 1 program switch + 1 flush instead of 2N + N.
	 * @param {object} light - the Light2d instance to render
	 */
	drawLight(light) {
		if (this._lightShader === undefined) {
			this._lightShader = new RadialGradientEffect(this);
		}
		// `setBatcher("quad", _lightShader)` switches the quad batcher's
		// shader to the radial gradient if it isn't already bound (and
		// flushes any sprite vertices queued under the previous shader).
		// On subsequent back-to-back `drawLight` calls this is a no-op,
		// so the lights pile into the same vertex buffer.
		const batcher = this.setBatcher("quad", this._lightShader);
		batcher.addQuad(
			this._getLightAtlas(),
			light.pos.x,
			light.pos.y,
			light.width,
			light.height,
			0,
			0,
			1,
			1,
			// pack the light's color (RGB) and intensity (A) into the
			// vertex tint — the shader's `apply()` reads `color.rgb` and
			// `color.a` as the per-light values.
			light.color.toUint32(light.intensity),
		);
		// Note: we deliberately do NOT switch back to the default shader
		// here. The next `setBatcher` call (sprites, primitives, etc.)
		// will reconcile to the right shader on its own (see
		// `setBatcher`), and that's what unlocks the cross-light batch.
	}

	/**
	 * Lazy-init a shared 1×1 white `TextureAtlas` used as the source
	 * texture for `drawLight`'s procedural shader. The shader ignores
	 * the sampled color, but `addQuad`'s vertex format includes a
	 * texture-unit attribute so we still need a real texture; sharing
	 * one across every light keeps them on the same multi-texture slot
	 * (no flush on light switch).
	 * @returns {TextureAtlas}
	 * @ignore
	 * @internal
	 */
	_getLightAtlas() {
		if (this._lightAtlas === undefined) {
			// build a 1×1 white canvas — TextureAtlas wants an image-like
			// source that can feed `gl.texImage2D` directly.
			const canvas = globalThis.document
				? globalThis.document.createElement("canvas")
				: new OffscreenCanvas(1, 1);
			canvas.width = 1;
			canvas.height = 1;
			const ctx = canvas.getContext("2d");
			ctx.fillStyle = "#fff";
			ctx.fillRect(0, 0, 1, 1);
			this._lightAtlas = new TextureAtlas(
				createAtlas(1, 1, "lightWhite", "no-repeat"),
				canvas,
			);
		}
		return this._lightAtlas;
	}

	/**
	 * Capture the current frame — everything drawn to the active framebuffer so
	 * far — into a {@link Texture2d}, entirely on the GPU (a `copyTexImage2D` on
	 * the first capture, then `copyTexSubImage2D` to refresh in place; no
	 * `readPixels` round-trip or pipeline stall). The fourth member of the
	 * {@link Renderer#toDataURL} / {@link Renderer#toBlob} /
	 * {@link Renderer#toImageBitmap} family — "the current frame as X" — and the
	 * only one whose result never leaves the GPU.
	 *
	 * The capture reflects the framebuffer **at the call site** (a `flush()`
	 * runs first), so call it right before drawing the surface that needs the
	 * backdrop — the classic back-buffer-copy placement model. It reads whichever
	 * framebuffer is currently bound: the backbuffer normally, or a camera's
	 * post-effect FBO during that pass — so it works under both `Camera2d` and
	 * `Camera3d`. Under a perspective camera, "drawn so far" is draw order, not
	 * depth order: capture after the opaque scene, just before the refracting
	 * surface (the usual opaque-scene-texture guidance).
	 *
	 * Feed the result straight into a custom post-effect:
	 * ```js
	 * effect.setTexture("uScene", renderer.toFrameTexture());
	 * ```
	 * {@link ShaderEffect#setTexture} binds the **live** handle, so re-capturing
	 * each frame into the shared slot refreshes what the shader samples without
	 * any re-bind.
	 *
	 * Color only, captured into an RGB texture (opaque — samples with alpha = 1);
	 * the framebuffer's depth is not captured. By default the result is a shared,
	 * renderer-owned texture valid until the next parameterless call — pass
	 * `options.target` for an independent, caller-owned capture (e.g. two
	 * captures alive in the same frame).
	 * @param {object} [options]
	 * @param {Texture2d|null} [options.target] - controls the destination: omit
	 *   for the shared renderer slot (default); pass a capture previously
	 *   returned by this method to refresh it in place; pass `null` to mint a
	 *   fresh, caller-owned capture (for two independent captures in one frame —
	 *   `destroy()` it yourself when done)
	 * @param {Bounds|{x: number, y: number, width: number, height: number}} [options.region] - capture
	 *   only this sub-region (framebuffer pixels, bottom-left origin); a smaller
	 *   region is a proportionally cheaper copy. Defaults to the whole framebuffer.
	 *   A {@link Bounds} (or any `{x, y, width, height}`); from a {@link Rect}
	 *   pass `rect.getBounds()`.
	 * @returns {Texture2d} a GPU-resident texture holding the captured frame
	 * @example
	 * // a water surface that refracts the scene rendered behind it
	 * draw(renderer) {
	 *     const scene = renderer.toFrameTexture();          // grab the backdrop
	 *     this.shader.setTexture("uScene", scene);          // live-bound
	 *     super.draw(renderer);                             // draw the rippling water
	 * }
	 */
	toFrameTexture(options = {}) {
		// Capturing is machinery, not a scene draw. It flushes and re-enters
		// `setBatcher("quad")` below, and an unguarded hook there would open an
		// advanced-blend bracket MID-CAPTURE — binding the freshly-cleared
		// offscreen right before the copy reads the framebuffer, so the capture
		// would hold nothing. Reachable from user code: `drawImage` refreshes
		// the shared capture for any custom shader that samples the screen,
		// before its own setBatcher, so a renderable with an advanced mode and
		// such an effect hits this path every draw.
		// Resolved off the PROTOTYPE and restored by value, not by decrement —
		// see the WebGPU twin: specs borrow this method onto a recording stub
		// that has neither the counter nor the implementation half.
		const busy = this._advancedBlendBusy;
		this._advancedBlendBusy = (busy ?? 0) + 1;
		try {
			return WebGLRenderer.prototype._toFrameTexture.call(this, options);
		} finally {
			this._advancedBlendBusy = busy;
		}
	}

	/**
	 * @ignore
	 * @internal
	 */
	_toFrameTexture(options = {}) {
		const gl = this.gl;
		const canvas = this.getCanvas();

		// resolve the capture rect in framebuffer pixels (default: everything).
		// a Bounds — or any object exposing numeric x/y/width/height (a Rect via
		// `rect.getBounds()`).
		let x = 0;
		let y = 0;
		let w = canvas.width;
		let h = canvas.height;
		const region = options.region;
		if (typeof region !== "undefined") {
			const rx = region.x;
			const ry = region.y;
			// clamp the ORIGIN into the framebuffer first, then size to what
			// remains — an out-of-range x/y must not push x+w past the edge, or
			// copyTex(Sub)Image2D throws INVALID_VALUE. Missing width/height
			// defaults to "the rest of the framebuffer from x/y".
			x = Math.min(Math.max(0, Math.floor(rx || 0)), canvas.width - 1);
			y = Math.min(Math.max(0, Math.floor(ry || 0)), canvas.height - 1);
			const rw = Number.isFinite(region.width)
				? Math.ceil(region.width)
				: canvas.width - x;
			const rh = Number.isFinite(region.height)
				? Math.ceil(region.height)
				: canvas.height - y;
			w = Math.max(1, Math.min(canvas.width - x, rw));
			h = Math.max(1, Math.min(canvas.height - y, rh));
		}

		// flush pending geometry so the capture holds everything drawn so far
		this.flush();

		// a non-null `target` must be a capture THIS renderer previously returned
		// (a FrameTexture) — any other Texture2d has no `glTexture`, and a capture
		// from a different renderer would delete textures on the wrong GL context
		if (typeof options.target !== "undefined" && options.target !== null) {
			if (!(options.target instanceof FrameTexture)) {
				throw new Error(
					"WebGLRenderer.toFrameTexture: `target` must be a capture returned by this method",
				);
			}
			if (options.target._renderer !== this) {
				throw new Error(
					"WebGLRenderer.toFrameTexture: `target` belongs to a different renderer",
				);
			}
		}

		// destination:
		//  - no `target`            → the shared, renderer-owned slot (default;
		//                             overwritten by the next parameterless call)
		//  - `target: null`         → mint a fresh, caller-owned capture (for two
		//                             independent captures alive in one frame)
		//  - `target: <capture>`    → refresh that caller-owned capture in place
		const shared = typeof options.target === "undefined";
		let frame = shared
			? this.getSharedFrameTexture()
			: options.target === null
				? undefined
				: options.target;

		// (re)allocate when missing, resized, or the GL handle went stale (a
		// context-loss/restore cycle deletes it — gl.isTexture catches that)
		if (
			typeof frame === "undefined" ||
			frame.width !== w ||
			frame.height !== h ||
			frame.glTexture === null ||
			gl.isTexture(frame.glTexture) === false
		) {
			if (typeof frame !== "undefined") {
				frame.destroy();
				frame.width = w;
				frame.height = h;
			} else {
				frame = new FrameTexture(this, w, h);
			}
			if (shared) {
				this._frameTexture = frame;
			}
		}

		// Copy the bound framebuffer into the capture texture — the standard
		// framebuffer→texture path.
		// An RGB internalformat is a valid subset of BOTH an alpha-less default
		// framebuffer (melonJS creates the context with `alpha: transparent`,
		// usually false) AND an RGBA camera FBO, so the copy never trips
		// INVALID_OPERATION on a format mismatch, and the capture samples as an
		// opaque backdrop (alpha = 1). Unlike a blitFramebuffer destination —
		// which some drivers won't sample in the same frame — an RGB texture
		// copied this way samples reliably everywhere.
		const batcher = this.setBatcher("quad");
		// the renderer's top unit, not the batcher's — a scratch bind is GL
		// state, so it must not move when a batcher resolves a smaller cap
		const unit = this.maxTextures - 1;

		// a multisampled post-effect capture cannot be read directly
		// (copyTex*Image2D from an MSAA framebuffer is INVALID_OPERATION) —
		// resolve it and point the READ binding at the single-sampled half
		// for the copy, then rebind the render half for continued drawing
		const activeTarget =
			this._effectPassDepth > 0
				? this._renderTargetPool.getCaptureTarget()
				: null;
		const msaaActive = activeTarget?.renderFramebuffer ? activeTarget : null;
		if (msaaActive) {
			msaaActive.resolve();
			gl.bindFramebuffer(gl.READ_FRAMEBUFFER, msaaActive.framebuffer);
		}

		if (frame.glTexture === null) {
			// first capture into this slot: copyTexImage2D allocates the RGB
			// storage AND copies in one call
			frame.glTexture = gl.createTexture();
			batcher.bindTexture2D(frame.glTexture, unit, false);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
			gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGB, x, y, w, h, 0);
		} else {
			// steady state: refresh the existing storage in place — no per-frame
			// reallocation. Same size is guaranteed (a size change nulls
			// glTexture above via the shared-slot realloc), so copyTexSubImage2D
			// is valid and copies RGB from either an RGB or RGBA source.
			batcher.bindTexture2D(frame.glTexture, unit, false);
			gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, x, y, w, h);
		}

		if (msaaActive) {
			// back to rasterizing into the multisampled half
			msaaActive.bind();
		}

		// the scratch bind clobbered GL `unit` OUTSIDE the shared texture-cache
		// accounting (bound directly, not via allocateTextureUnit), so other
		// batchers' unit caches don't know it changed — invalidate it on all of
		// them (see invalidateTextureUnit).
		this.invalidateTextureUnit(unit);

		return frame;
	}

	/**
	 * The renderer-owned shared frame-capture slot, minted lazily so it has a
	 * stable identity from the moment an effect is constructed — before the
	 * first actual capture fills it (`glTexture` stays `null` until then, and
	 * the live-sampler bind path skips unfilled captures). `ShaderEffect`
	 * wires `: screen_texture` annotated samplers to this object.
	 * @returns {FrameTexture} the shared capture slot
	 * @ignore
	 * @internal
	 */
	getSharedFrameTexture() {
		if (typeof this._frameTexture === "undefined") {
			const canvas = this.getCanvas();
			this._frameTexture = new FrameTexture(this, canvas.width, canvas.height);
		}
		return this._frameTexture;
	}

	/**
	 * Forget the cached binding for GL texture `unit` on every batcher (except
	 * `except`). Call this whenever a texture is bound to a unit **directly** —
	 * bypassing the shared texture cache and its `GPU_TEXTURE_CACHE_RESET`
	 * coordination — as {@link WebGLRenderer#toFrameTexture}'s capture copy and
	 * {@link ShaderEffect#_prepareTextures}'s reserved extra samplers both do.
	 * Those binds land on the TOP of the unit range, which overlaps the lit quad
	 * batcher's normal-map units; without invalidation a later lit draw would
	 * assume its normal map is still resident there and skip re-binding it,
	 * sampling the directly-bound texture as a normal map (wrong lighting).
	 * @param {number} unit - the GL texture unit that was clobbered
	 * @param {WebGLBatcher} [except] - a batcher to skip (the one that just bound the
	 *   texture — its own cache is already accurate)
	 * @ignore
	 * @internal
	 */
	invalidateTextureUnit(unit, except) {
		for (const b of this.batchers.values()) {
			if (b !== except) {
				b.invalidateUnit?.(unit);
			}
		}
	}

	/**
	 * Begin capturing rendering to an offscreen FBO for post-effect processing.
	 * @param {Renderable} renderable - the renderable requesting post-effect processing
	 * @returns {boolean} true if FBO capture started, false if skipped
	 * @ignore
	 * @internal
	 */
	beginPostEffect(renderable) {
		// filter to only enabled effects
		const effects = renderable.postEffects.filter((fx) => {
			return fx.enabled !== false;
		});
		if (effects.length === 0) {
			this.customShader = undefined;
			return false;
		}
		// single effect on non-managed renderable: fast path via customShader (no FBO)
		if (effects.length === 1 && !renderable._postEffectManaged) {
			this.customShader = effects[0];
			return false;
		}

		// multi-pass FBO path: clear customShader so children render with default shader
		this.customShader = undefined;

		const isCamera = renderable._postEffectManaged;
		const canvas = this.getCanvas();
		const w = canvas.width;
		const h = canvas.height;

		// flush pending draws BEFORE creating/resizing FBOs,
		// since FBO construction temporarily changes GL framebuffer bindings
		this.flush();
		this.save();
		// save the current projection (not part of the render state stack) —
		// one preallocated slot per nesting depth, so nested passes each
		// restore their own without per-frame allocation
		let savedProjection = this._effectProjectionStack[this._effectPassDepth];
		if (typeof savedProjection === "undefined") {
			savedProjection = this._effectProjectionStack[this._effectPassDepth] =
				new Matrix3d();
		}
		savedProjection.copy(this.projectionMatrix);
		this._effectPassDepth++;

		const rt = this._renderTargetPool.begin(isCamera, effects.length, w, h);
		// FBO creation/resize binds (then nulls) TEXTURE0 — invalidate EVERY
		// batcher's cache for that unit, not just the current one: a batcher
		// that isn't current right now would otherwise keep trusting its stale
		// `boundTextures[0]` and skip the re-bind, sampling an unbound texture
		// (opaque black) for as long as the post effect is active
		this.invalidateTextureUnit(0);
		rt.bind();
		this.setViewport(0, 0, w, h);
		this.disableScissor();
		this.setGlobalAlpha(1.0);
		this.setBlendMode("normal");

		if (isCamera) {
			this.clear();
		} else {
			this.clearRenderTarget();
		}
		return true;
	}

	/**
	 * @ignore
	 * @internal
	 */
	endPostEffect(renderable) {
		// filter to only enabled effects
		const effects = renderable.postEffects.filter((fx) => {
			return fx.enabled !== false;
		});
		if (effects.length === 0) {
			return;
		}
		// single effect on non-managed renderable used customShader — no FBO to unbind
		if (effects.length === 1 && !renderable._postEffectManaged) {
			return;
		}

		// Replay anything this pass queued while its own target is still bound.
		// The entries were recorded for THIS target under THIS projection, and
		// the key is about to be handed back to the pool — so this is the only
		// moment they can be drawn where they belong.
		this.flushTransparentPass();

		const isCamera = renderable._postEffectManaged;
		const rt1 = this._renderTargetPool.getCaptureTarget();
		const rt2 = this._renderTargetPool.getPingPongTarget();
		const keepBlend = !isCamera;
		const canvas = this.getCanvas();
		const w = canvas.width;
		const h = canvas.height;

		this.flush();

		// `screen_texture` shader builtin (back-buffer copy semantics):
		// refresh the shared frame capture the effects' live samplers are
		// wired to. For a CAMERA effect the "screen" is the scene itself —
		// capture the still-bound camera FBO before unbinding it. For a
		// renderable's effect chain the "screen" is everything drawn behind
		// it — captured from the parent target after it is rebound below.
		const needsScreenTexture = effects.some((fx) => {
			return fx._screenTextureUniforms?.length > 0;
		});
		if (needsScreenTexture && isCamera) {
			this.toFrameTexture();
		}

		rt1.unbind();

		// get parent render target for rebinding after blits
		const parentRT = this._renderTargetPool.end();

		// clip to camera viewport for non-default cameras
		if (isCamera && renderable.isDefault === false) {
			this.clipRect(
				renderable.screenX,
				renderable.screenY,
				renderable.width,
				renderable.height,
			);
		}

		// rebind parent render target (or screen) and restore viewport.
		// The attachments we're returning to are different physical
		// buffers than the FBO we just unbound — let batchers with
		// target-scoped state invalidate it (e.g. `MeshBatcher`'s lazy
		// depth clear flag).
		if (parentRT) {
			parentRT.bind();
		}
		this.setViewport(0, 0, w, h);
		emit(RENDER_TARGET_CHANGED, this);

		// see needsScreenTexture above — the scene behind this renderable
		if (needsScreenTexture && !isCamera) {
			this.toFrameTexture();
		}

		if (effects.length === 1) {
			this.blitEffect(rt1.texture, 0, 0, w, h, effects[0], keepBlend);
		} else {
			// multi-pass: ping-pong between two render targets
			let src = rt1;
			let dst = rt2;

			for (let i = 0; i < effects.length - 1; i++) {
				dst.bind();
				this.setViewport(0, 0, w, h);
				this.clearRenderTarget();
				this.blitEffect(src.texture, 0, 0, w, h, effects[i]);
				dst.unbind();
				const tmp = src;
				src = dst;
				dst = tmp;
			}

			// rebind parent for final blit
			if (parentRT) {
				parentRT.bind();
			}
			this.setViewport(0, 0, w, h);

			this.blitEffect(
				src.texture,
				0,
				0,
				w,
				h,
				effects[effects.length - 1],
				keepBlend,
			);
		}

		if (isCamera && renderable.isDefault === false) {
			this.disableScissor();
		}

		// restore renderer state and projection saved in beginPostEffect
		this.restore();
		this._effectPassDepth--;
		this.projectionMatrix.copy(
			this._effectProjectionStack[this._effectPassDepth],
		);
		this.currentBatcher.setProjection(this.projectionMatrix);
	}

	/**
	 * Say once, per excluded path and mode, that an advanced blend mode is
	 * falling back to `"normal"`. A silent data-dependent fallback is exactly
	 * what the advanced-blend work exists to remove, so the exclusions are
	 * loud.
	 * @param {string} what - the excluded path, named for the message
	 * @ignore
	 * @internal
	 */
	_warnAdvancedBlendFallback(what) {
		const key = `${what}:${this.currentBlendMode}`;
		if (this._advancedBlendWarned.has(key)) {
			return;
		}
		this._advancedBlendWarned.add(key);
		console.warn(
			`WebGLRenderer: blend mode "${this.currentBlendMode}" is not supported for ${what} and falls back to "normal"`,
		);
	}

	/**
	 * Close a pending advanced-blend bracket, if one is open and we are not
	 * already inside the bracket machinery. The safe form to call from any
	 * path that is about to disturb the destination.
	 * @ignore
	 * @internal
	 */
	_drainAdvancedBlend() {
		if (this._advancedBlendOpen === true && this._advancedBlendBusy === 0) {
			this._closeAdvancedBlend();
		}
	}

	/**
	 * Open an advanced-blend bracket: capture the destination, then redirect
	 * this draw into an offscreen target. Closed by {@link _closeAdvancedBlend}
	 * at the next draw, flush or clear.
	 * @returns {boolean} false when the effect could not be realized, in which
	 * case the draw proceeds unbracketed (plain source-over)
	 * @ignore
	 * @internal
	 */
	_openAdvancedBlend() {
		const gl = this.gl;
		const canvas = this.getCanvas();
		const w = canvas.width;
		const h = canvas.height;

		this._advancedBlendBusy++;

		if (typeof this._advancedBlendEffect === "undefined") {
			this._advancedBlendEffect = new BlendEffect(this);
		}
		if (this._advancedBlendEffect.enabled !== true) {
			// the shader did not compile on this device — degrade to plain
			// source-over rather than dropping the draw entirely
			this._advancedBlendBusy--;
			this._warnAdvancedBlendFallback("this device");
			return false;
		}
		this._advancedBlendEffect.setBlendMode(this.currentBlendMode);

		// drain everything queued before this draw, so the capture below
		// holds the true destination this draw blends against
		this.currentBatcher.flush();

		// a RENDERER-OWNED capture, not the shared `screen_texture` slot:
		// drawImage already refreshes that one for any custom shader that
		// samples the screen, and a renderable carrying both an advanced
		// mode and such an effect would otherwise have the two fighting
		// over a single texture
		this._advancedBlendCapture = this.toFrameTexture({
			target: this._advancedBlendCapture ?? null,
		});
		this._advancedBlendEffect.setTexture(
			"backdrop",
			this._advancedBlendCapture,
		);

		// remember where this draw was headed before redirecting it
		this._advancedBlendParent =
			this._effectPassDepth > 0
				? this._renderTargetPool.getCaptureTarget()
				: null;

		if (typeof this._advancedBlendTarget === "undefined") {
			this._advancedBlendTarget = new WebGLRenderTarget(gl, w, h, {
				samples: 0,
			});
		} else {
			this._advancedBlendTarget.resize(w, h);
		}
		// FBO creation/resize binds (then nulls) TEXTURE0 — invalidate every
		// batcher's cache for that unit, as beginPostEffect does
		this.invalidateTextureUnit(0);
		this._advancedBlendTarget.bind();
		this.setViewport(0, 0, w, h);
		this.clearRenderTarget();

		// the offscreen carries its own, zeroed stencil: an armed mask tests
		// EQUAL against a non-zero reference and would reject every fragment,
		// so the draw would silently vanish. The COMPOSITE is clipped instead,
		// which is where the mask belongs.
		this._advancedBlendStencil = gl.isEnabled(gl.STENCIL_TEST);
		if (this._advancedBlendStencil === true) {
			gl.disable(gl.STENCIL_TEST);
		}

		this._advancedBlendOpen = true;
		this._advancedBlendBusy--;
		return true;
	}

	/**
	 * Close an advanced-blend bracket: drain the offscreen, return to the
	 * parent target, and composite through {@link BlendEffect}.
	 * @ignore
	 * @internal
	 */
	_closeAdvancedBlend() {
		const gl = this.gl;
		const canvas = this.getCanvas();
		const w = canvas.width;
		const h = canvas.height;

		this._advancedBlendBusy++;
		this._advancedBlendOpen = false;

		// drain the bracketed draw into the offscreen
		this.currentBatcher.flush();

		this._advancedBlendTarget.unbind();
		if (this._advancedBlendParent !== null) {
			this._advancedBlendParent.bind();
		}
		this._advancedBlendParent = null;
		this.invalidateTextureUnit(0);
		this.setViewport(0, 0, w, h);
		emit(RENDER_TARGET_CHANGED, this);

		if (this._advancedBlendStencil === true) {
			gl.enable(gl.STENCIL_TEST);
		}

		// The effect emits PREMULTIPLIED colour, so ordinary source-over
		// finishes the composite and yields a correct output alpha for free.
		// Restore the token afterwards without re-touching GL state: the
		// next bracket's offscreen draw wants exactly this source-over, and
		// a genuinely different next mode will miss the cache and re-apply.
		const mode = this.currentBlendMode;
		this.setBlendMode("normal", true);
		this.blitEffect(
			this._advancedBlendTarget.texture,
			0,
			0,
			w,
			h,
			this._advancedBlendEffect,
			true,
		);
		this.currentBatcher.flush();
		this.currentBlendMode = mode;

		this._advancedBlendBusy--;
	}

	/**
	 * Blit a texture to the screen through a shader effect.
	 * Draws a screen-aligned quad using the given texture as source
	 * and the given shader for post-processing (e.g. scanlines, desaturation).
	 * @param {WebGLTexture} source - the source texture to blit
	 * @param {number} x - destination x position
	 * @param {number} y - destination y position
	 * @param {number} width - destination width
	 * @param {number} height - destination height
	 * @param {ShaderEffect} shader - the shader effect to apply
	 * @param {boolean} [keepBlend=false] - if true, keep current blend mode (for sprite compositing)
	 */
	blitEffect(source, x, y, width, height, shader, keepBlend = false) {
		// A blit is compositing machinery, not a scene draw: it must never
		// open an advanced-blend bracket of its own. That covers both the
		// post-effect chain's ping-pong blits (which run under the
		// renderable's live blend mode) and this bracket's own composite.
		this._advancedBlendBusy++;
		// flush any pending draws
		this.flush();

		const batcher = this.setBatcher("quad");

		// set up a screen-space ortho projection for the blit quad
		// (not the camera's world projection which includes scrolling)
		_savedTransform.copy(this.currentTransform);
		_savedProjection.copy(this.projectionMatrix);
		this.currentTransform.identity();
		this.projectionMatrix.ortho(0, width, height, 0, -1, 1);
		batcher.setProjection(this.projectionMatrix);

		// disable blending for camera blits (render target is fully composited).
		// keep blending for per-sprite blits (transparent areas must not overwrite scene).
		if (!keepBlend) {
			this.setBlendEnabled(false);
		}
		batcher.blitTexture(source, x, y, width, height, shader);
		if (!keepBlend) {
			this.setBlendEnabled(true);
		}

		// restore state
		this.currentTransform.copy(_savedTransform);
		this.projectionMatrix.copy(_savedProjection);
		batcher.setProjection(this.projectionMatrix);
		this._advancedBlendBusy--;
	}

	/**
	 * set/change the current projection matrix (WebGL only)
	 * @param {Matrix3d} matrix - the new projection matrix
	 */
	setProjection(matrix) {
		// flush pending draws before switching projection
		this.flush();
		super.setProjection(matrix);
		this.currentBatcher.setProjection(matrix);
	}

	/**
	 * Sets the WebGL viewport, which specifies the affine transformation of x and y from normalized device coordinates to window coordinates
	 * @param {number} [x = 0] - x the horizontal coordinate for the lower left corner of the viewport origin
	 * @param {number} [y = 0] - y the vertical coordinate for the lower left corner of the viewport origin
	 * @param {number} [w = width of the canvas] - the width of viewport
	 * @param {number} [h = height of the canvas] - the height of viewport
	 */
	setViewport(
		x = 0,
		y = 0,
		w = this.getCanvas().width,
		h = this.getCanvas().height,
	) {
		this.gl.viewport(x, y, w, h);
	}

	/**
	 * Clear the current render target to transparent black.
	 *
	 * Only clears the color buffer — `setMask()` clears `STENCIL_BUFFER_BIT`
	 * itself when starting a mask, and including it here would emit a WebGL
	 * `Clear called for non-existing buffers` warning when the active FBO
	 * has no stencil attachment (rare, but happens on drivers that fail
	 * the depth+stencil renderbuffer attachment for the FBO).
	 */
	clearRenderTarget() {
		const gl = this.gl;
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);
		emit(RENDER_TARGET_CHANGED, this);
	}

	/**
	 * Enable the scissor test with the given rectangle.
	 * @param {number} x - x coordinate of the scissor rectangle
	 * @param {number} y - y coordinate of the scissor rectangle
	 * @param {number} width - width of the scissor rectangle
	 * @param {number} height - height of the scissor rectangle
	 */
	enableScissor(x, y, width, height) {
		const gl = this.gl;
		const canvas = this.getCanvas();
		// Derive the screen-space AABB via `Bounds.addFrame`, which
		// short-circuits the 4-corner walk when `currentTransform` is
		// identity. `currentScissor` stores screen-space coords directly
		// so save/restore can re-apply without re-running transform math
		// and so a scaled or rotated parent transform is honored — not
		// just translation. Issue #1349.
		const aabb = this._clipAABB;
		aabb.clear();
		aabb.addFrame(x, y, x + width, y + height, this.currentTransform);
		const sx = Math.floor(aabb.min.x);
		const sy = Math.floor(aabb.min.y);
		const sw = Math.ceil(aabb.max.x - sx);
		const sh = Math.ceil(aabb.max.y - sy);
		this.flush();
		gl.enable(gl.SCISSOR_TEST);
		this._scissorActive = true;
		gl.scissor(sx, canvas.height - sh - sy, sw, sh);
		this.currentScissor[0] = sx;
		this.currentScissor[1] = sy;
		this.currentScissor[2] = sw;
		this.currentScissor[3] = sh;
	}

	/**
	 * Disable the scissor test, allowing rendering to the full viewport.
	 */
	disableScissor() {
		if (this._scissorActive === true) {
			// drain quads batched under the scissor BEFORE turning the test
			// off — GL scissor applies at draw time, not at batch time
			// (mirrors enableScissor / clipRect / restore, which all flush
			// for exactly this reason)
			this.flush();
		}
		this.gl.disable(this.gl.SCISSOR_TEST);
		this._scissorActive = false;
	}

	/**
	 * Enable or disable alpha blending.
	 * @param {boolean} enable - true to enable blending, false to disable
	 */
	setBlendEnabled(enable) {
		if (enable) {
			this.gl.enable(this.gl.BLEND);
		} else {
			this.gl.disable(this.gl.BLEND);
		}
	}

	/**
	 * Clear the frame buffer
	 */
	clear() {
		// a clear wipes the destination, so any bracketed draw still waiting
		// to be composited has to land first or it would appear ON TOP of the
		// clear instead of under it
		this._drainAdvancedBlend();
		const gl = this.gl;
		const clearColor = this.backgroundColor.toArray();
		gl.clearColor(clearColor[0], clearColor[1], clearColor[2], clearColor[3]);
		this.lineWidth = 1;
		// Color + stencil are cleared here. Depth (if any batcher cares
		// — currently only `MeshBatcher`) is cleared lazily on the first
		// draw of its mode, via `_onTargetChanged` → `updatePassState()`.
		gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
		emit(RENDER_TARGET_CHANGED, this);
	}

	/**
	 * Clears the gl context with the given color.
	 * @param {Color|string} [color="#000000"] - CSS color.
	 * @param {boolean} [opaque=false] - Allow transparency [default] or clear the surface completely [true]
	 */
	clearColor(color = "#000000", opaque = false) {
		// see clear() — also covers clearRect, which routes through here
		this._drainAdvancedBlend();
		let glArray;
		const gl = this.gl;

		if (color instanceof Color) {
			glArray = color.toArray();
		} else {
			const _color = colorPool.get();
			// reuse temporary the renderer default color object
			glArray = _color.parseCSS(color).toArray();
			colorPool.release(_color);
		}

		// clear gl context with the specified color
		gl.clearColor(
			glArray[0],
			glArray[1],
			glArray[2],
			opaque === true ? 1.0 : glArray[3],
		);
		gl.clear(gl.COLOR_BUFFER_BIT);
	}

	/**
	 * Erase the pixels in the given rectangular area by setting them to transparent black (rgba(0,0,0,0)).
	 * @param {number} x - x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 */
	clearRect(x, y, width, height) {
		this.save();
		this.clipRect(x, y, width, height);
		// actual transparent black, per the JSDoc above and the Canvas
		// renderer's native clearRect — the bare clearColor() default
		// ("#000000") parses with alpha 1 and would paint OPAQUE black
		this.clearColor("rgba(0,0,0,0)");
		this.restore();
	}

	/**
	 * Draw an image to the gl context
	 * @param {HTMLImageElement|SVGImageElement|HTMLVideoElement|HTMLCanvasElement|ImageBitmap|OffscreenCanvas|VideoFrame|CompressedImage} image - An element to draw into the context.
	 * @param {number} sx - The X coordinate of the top left corner of the sub-rectangle of the source image to draw into the destination context.
	 * @param {number} sy - The Y coordinate of the top left corner of the sub-rectangle of the source image to draw into the destination context.
	 * @param {number} sw - The width of the sub-rectangle of the source image to draw into the destination context. If not specified, the entire rectangle from the coordinates specified by sx and sy to the bottom-right corner of the image is used.
	 * @param {number} sh - The height of the sub-rectangle of the source image to draw into the destination context.
	 * @param {number} dx - The X coordinate in the destination canvas at which to place the top-left corner of the source image.
	 * @param {number} dy - The Y coordinate in the destination canvas at which to place the top-left corner of the source image.
	 * @param {number} dw - The width to draw the image in the destination canvas. This allows scaling of the drawn image. If not specified, the image is not scaled in width when drawn.
	 * @param {number} dh - The height to draw the image in the destination canvas. This allows scaling of the drawn image. If not specified, the image is not scaled in height when drawn.
	 * @example
	 * // Position the image on the canvas:
	 * renderer.drawImage(image, dx, dy);
	 * // Position the image on the canvas, and specify width and height of the image:
	 * renderer.drawImage(image, dx, dy, dWidth, dHeight);
	 * // Clip the image and position the clipped part on the canvas:
	 * renderer.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
	 */
	drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh) {
		if (typeof sw === "undefined") {
			sw = dw = image.width;
			sh = dh = image.height;
			dx = sx;
			dy = sy;
			sx = 0;
			sy = 0;
		} else if (typeof dx === "undefined") {
			dx = sx;
			dy = sy;
			dw = sw;
			dh = sh;
			sw = image.width;
			sh = image.height;
			sx = 0;
			sy = 0;
		}

		if (this.settings.subPixel === false) {
			// clamp to pixel grid
			dx |= 0;
			dy |= 0;
		}

		// dispatch to the lit batcher only when the scene actually has
		// active lights AND this sprite has a normal map. Otherwise the
		// unlit `quad` batcher is faster (full texture-unit capacity, no
		// paired normal upload, no per-fragment lighting math).
		// Back-buffer copy semantics for the `screen_texture` shader
		// builtin: refresh the shared frame capture with everything drawn so
		// far BEFORE this sprite's own quad, so the effect samples the scene
		// behind it. Must run before setBatcher below — toFrameTexture
		// flushes and switches to the quad batcher internally.
		if (this.customShader?._screenTextureUniforms?.length > 0) {
			this.toFrameTexture();
		}

		const useLit =
			this.batchers.has("litQuad") &&
			this.activeLightCount > 0 &&
			this.currentNormalMap !== null;
		this.setBatcher(useLit ? "litQuad" : "quad");

		const shader = this.customShader;
		if (shader != null) {
			this.currentBatcher.useShader(shader);
			// (re)bind any extra textures a ShaderEffect declared via setTexture
			shader._prepareTextures?.(this.currentBatcher);
		}

		const texture = this.cache.get(image);
		// Video sources need their GL texture refreshed as the video plays.
		// When a Sprite tracks the element via requestVideoFrameCallback it
		// stamps a monotonic `version` counter on it (same duck-typed
		// convention as NoiseTexture2d) — re-upload only when a new frame was
		// actually presented, recorded per cached atlas so a shared element
		// drawn by several sprites uploads once per frame. Without the stamp
		// (no rVFC support / manual drawImage callers), or on the lit path
		// (each batcher owns a separate GL texture — one shared record would
		// go stale across them), keep the legacy per-frame force re-upload.
		let reupload = false;
		if (typeof image.videoWidth !== "undefined") {
			if (image.version === undefined || useLit) {
				reupload = true;
			} else if (texture._videoFrameVersion !== image.version) {
				texture._videoFrameVersion = image.version;
				reupload = true;
			}
		}
		const uvs = texture.getUVs(sx, sy, sw, sh);
		if (useLit) {
			this.currentBatcher.addQuad(
				texture,
				dx,
				dy,
				dw,
				dh,
				uvs[0],
				uvs[1],
				uvs[2],
				uvs[3],
				this.currentTint.toUint32(this.getGlobalAlpha()),
				reupload,
				this.currentNormalMap,
			);
		} else {
			this.currentBatcher.addQuad(
				texture,
				dx,
				dy,
				dw,
				dh,
				uvs[0],
				uvs[1],
				uvs[2],
				uvs[3],
				this.currentTint.toUint32(this.getGlobalAlpha()),
				reupload,
			);
		}

		if (typeof shader === "object") {
			this.currentBatcher.useShader(this.currentBatcher.defaultShader);
		}
	}

	/**
	 * Draw a pattern within the given rectangle.
	 * @param {TextureAtlas} pattern - Pattern object
	 * @param {number} x - x position where to draw the pattern
	 * @param {number} y - y position where to draw the pattern
	 * @param {number} width - width of the pattern
	 * @param {number} height - height of the pattern
	 * @see WebGLRenderer#createPattern
	 */
	drawPattern(pattern, x, y, width, height) {
		const uvs = pattern.getUVs("0,0," + width + "," + height);
		this.setBatcher("quad");
		this.currentBatcher.addQuad(
			pattern,
			x,
			y,
			width,
			height,
			uvs[0],
			uvs[1],
			uvs[2],
			uvs[3],
			this.currentTint.toUint32(this.getGlobalAlpha()),
		);
	}

	/**
	 * Draw a textured triangle mesh.
	 * Enables hardware depth testing and backface culling for the
	 * duration of the draw, then restores the previous GL state. Large
	 * meshes are automatically chunked across multiple draw calls to
	 * fit the vertex/index buffer limits.
	 *
	 * Multi-material meshes (OBJ files with multiple `usemtl` groups +
	 * a bound MTL) don't add any per-material draw calls here — the
	 * per-material colors are baked into `mesh.vertexColors` at
	 * construction time and pushed through the batcher's per-vertex
	 * `aColor` attribute. `mesh.tint` still multiplies on top at draw
	 * time, so flash / fade / team-color via `setTint` work the same
	 * as single-material. (The chunking-for-buffer-limits behavior
	 * above still applies to multi-material meshes as well.)
	 * @param {Mesh} mesh - a Mesh renderable or compatible object
	 * @param {Matrix3d} [modelMatrix] - where the mesh sits in the world. When
	 * given, the mesh is drawn from persistent model-space geometry and placed
	 * entirely by uniforms; when omitted, its vertices are taken as already
	 * positioned and accumulated through the batcher (the 2D-camera path).
	 */
	/**
	 * Draw one ground shadow per instance of an `InstancedMesh` (#1515), in a
	 * single call over the same instance buffer the mesh drew from.
	 * @param {InstancedMesh} mesh - the mesh casting the shadows
	 * @param {Matrix3d} shadowMatrix - the group matrix flattened onto the ground
	 * @param {object} quad - the shared shadow quad
	 * @ignore
	 * @internal
	 */
	/**
	 * Install a blend function for one draw WITHOUT touching
	 * `currentBlendMode`.
	 *
	 * The transparent pass sets state per entry and restores mesh-mode defaults
	 * afterwards, so the 2D blend cache must not learn about it — the next
	 * ordinary draw would then skip a `setBlendMode` it genuinely needs. Same
	 * deliberate cache bypass `MeshBatcher.beginBlendedDraw` documents.
	 * @param {string} mode - a blend mode token
	 * @ignore
	 * @internal
	 */
	applyBlendFunction(mode) {
		const gl = this.gl;
		// ALWAYS premultiplied, whatever `this.premultipliedAlpha` says — that
		// flag describes source TEXTURES, while every mesh vertex shader
		// premultiplies its own output unconditionally
		// (`vColor = vec4(tinted.rgb * tinted.a, tinted.a)`). Passing the flag
		// here selects SRC_ALPHA and multiplies by alpha a second time: a
		// half-faded white mesh over blue comes out at three-quarter blue
		// instead of full. It went unnoticed while decals were the only client,
		// because their source colour is black and 0 × anything is 0.
		const state = blendStateFor(normalizeBlendMode(mode), true);
		if (state === undefined) {
			// `"none"` is replace — the source overwrites the destination,
			// alpha included — and blending switched off is exactly that.
			// It reaches here because `blendMode` is an unvalidated property
			// and `"none"` is a supported token, so a faded mesh may legally
			// carry it; without this the `state.operation` read below throws
			// mid-frame. Matches the WebGPU pipeline cache, which builds a
			// replace pipeline from the same absent blend state.
			gl.disable(gl.BLEND);
			return;
		}
		gl.enable(gl.BLEND);
		gl.blendEquation(GL_BLEND_OP[state.operation]);
		gl.blendFunc(
			GL_BLEND_FACTOR[state.srcFactor],
			GL_BLEND_FACTOR[state.dstFactor],
		);
	}

	drawInstancedShadow(mesh, shadowMatrix, quad) {
		const tint = this.currentTint.toUint32(this.getGlobalAlpha());
		// held back for the transparent pass for the same reason a per-object
		// decal is — see `queueTransparent`
		if (this._transparentFlushing !== true) {
			this.queueTransparent(quad, shadowMatrix, tint, "normal", mesh);
			return;
		}
		this.setBatcher(quad.lit === true ? "litMesh" : "mesh");
		this.currentBatcher.drawInstancedShadow(mesh, shadowMatrix, tint, quad);
	}

	drawMesh(mesh, modelMatrix) {
		// A fully transparent mesh must not draw — and here that is not merely
		// a saving. The mesh path disables `GL_BLEND` (see `MeshBatcher.bind`),
		// so the alpha never reaches the blend stage: the shader multiplies the
		// colour by zero and the result is written OPAQUE BLACK. Setting
		// `alpha = 0` to hide a mesh painted a black silhouette of it.
		// `CanvasRenderer.drawMesh` has always skipped at this threshold, as do
		// eight other draw methods there; this is the same guard.
		if (this.getGlobalAlpha() < 1 / 255) {
			return;
		}

		const gl = this.gl;
		const retained = modelMatrix !== undefined;

		// Route a transparent draw into the transparent pass rather than
		// drawing it here. Hoisted above the batcher selection so an opaque
		// scene pays two property reads and one compare against a value this
		// method already computes further down for the draw itself.
		//
		// `mesh.transparent` is tri-state: `true` always (a soft-alpha TEXTURE
		// is invisible to the check below), `false` never, and unset means
		// "whenever this draw resolves to fractional alpha" — the opaque path
		// writes premultiplied colour with blending off, so a faded mesh
		// darkens toward black instead of fading, which is a bug rather than a
		// contract. `_blendedDraw` marks the internal decal quads.
		const packedTint = this.currentTint.toUint32(this.getGlobalAlpha());
		if (
			retained &&
			this._transparentFlushing !== true &&
			(mesh._blendedDraw === true ||
				mesh.transparent === true ||
				(mesh.transparent !== false && packedTint >>> 24 !== 0xff))
		) {
			this.queueTransparent(
				mesh,
				modelMatrix,
				packedTint,
				mesh._blendedDraw === true ? "normal" : mesh.blendMode,
				undefined,
			);
			return;
		}

		// Route to the lit or unlit mesh batcher. `mesh.lit` meshes use the
		// `LitMeshBatcher` (world-space normals + lighting); everything else
		// uses the lean unlit `MeshBatcher`. Both share the mesh-mode depth
		// state, so mixing them keeps inter-mesh occlusion correct.
		//
		// `setBatcher` delegates mesh-mode state setup (DEPTH_TEST enable,
		// LEQUAL, depthMask, BLEND off) to the batcher's `bind()`; the one-shot
		// per-target depth clear and the lit light upload happen per draw in
		// `updatePassState()`, since `bind()` only fires on a batcher
		// transition. Switching away restores non-mesh defaults via
		// `unbind()`. Consecutive same-kind `drawMesh` calls pay zero state cost
		// between them; the GPU's LEQUAL depth test resolves inter-mesh
		// occlusion per pixel against the accumulated depth buffer.
		this.setBatcher(mesh.lit === true ? "litMesh" : "mesh");

		// apply custom shader if set on the renderable (via preDraw) —
		// hostable only when it carries a live GL program (a WGSL-only
		// GLShader has none: keep the built-in shading, and say so once).
		// The warn keys on the DECLARED realization (isWebGL), not the live
		// program: a dual shader drawn during a lost-context window merely
		// lacks its program transiently and must neither warn nor consume
		// the one-shot for a later genuinely-unhostable shader.
		// A ShaderEffect is tried FIRST, and the order is the whole point:
		// `ShaderEffect.program` is a getter onto its own compiled program,
		// and that program is the QUAD realization — its vertex stage projects
		// straight from `uProjectionMatrix` and declares neither
		// `uModelMatrix` nor `uViewMatrix`. Binding it to a mesh passes the
		// `program != null` test below and then draws the geometry unplaced
		// and without the camera. So an effect is spliced into the mesh
		// shader instead, and only a shader that is NOT an effect may be
		// hosted as-is (#1658).
		const effectHosted =
			this.customShader != null &&
			this.currentBatcher.canHostEffect?.(this.customShader) === true;
		// set by EITHER hosting path, so the `finally` below restores the
		// default shader whichever one bound
		// null when the body could not be spliced or compiled against this
		// mesh family — the batcher has already said so, and the mesh falls
		// through to the built-in shading rather than drawing with nothing
		const spliced = effectHosted
			? this.currentBatcher.effectShaderFor(this.customShader)
			: null;
		let hostedShader = spliced !== null && spliced !== undefined;
		if (hostedShader) {
			this.currentBatcher.useShader(spliced);
			this.currentBatcher.replayEffectUniforms(this.customShader, spliced);
		} else if (this.customShader != null && this.customShader.program != null) {
			hostedShader = true;
			this.currentBatcher.useShader(this.customShader);
		} else if (
			this.customShader != null &&
			this.customShader.isWebGL !== true &&
			this._meshShaderWarned !== true
		) {
			/**
			 * @ignore
			 * @internal
			 */
			this._meshShaderWarned = true;
			console.warn(
				"melonJS: this custom shader cannot be hosted on a Mesh by the WebGL renderer (no compiled GLSL program) — the mesh draws with the built-in shading",
			);
		}

		// toggle backface culling per-mesh — varies across meshes in
		// the same run, so this stays here rather than in the batcher's
		// bind/unbind lifecycle.
		if (mesh.cullBackFaces) {
			gl.enable(gl.CULL_FACE);
			gl.cullFace(gl.BACK);
			// Retained geometry keeps its authored winding, so the axis bridge's
			// reflection (which mirrors handedness) is undone by flipping which
			// orientation counts as front-facing — equivalent to reversing every
			// triangle, without a second copy of the index data. Right-handed
			// meshes are bridged by a rotation, which preserves winding.
			gl.frontFace(retained && mesh.rightHanded !== true ? gl.CW : gl.CCW);
		}

		// finally: a throw mid-draw (e.g. a texture upload failing) must not
		// leak the cull toggle or leave the custom program bound — the NEXT
		// unshaded mesh would silently draw with it
		try {
			const tint = packedTint;
			if (
				mesh.instanceLayout !== undefined &&
				retained &&
				this.supportsInstancing === true
			) {
				// one geometry, N copies, one call — the per-instance records
				// carry the placement each copy differs by, while `modelMatrix`
				// places the group as a whole
				this.currentBatcher.drawInstancedMesh(mesh, modelMatrix, tint);
			} else if (retained) {
				this.currentBatcher.drawRetainedMesh(mesh, modelMatrix, tint);
			} else {
				this.currentBatcher.addMesh(mesh, tint);
				this.flush();
			}
		} finally {
			if (mesh.cullBackFaces) {
				gl.disable(gl.CULL_FACE);
				// restore the default orientation alongside the cull toggle:
				// leaving it at CW would leak to any later consumer of the
				// context that reads `gl_FrontFacing` or enables culling itself
				gl.frontFace(gl.CCW);
			}

			// revert to default shader if custom was applied
			if (hostedShader) {
				this.currentBatcher.useShader(this.currentBatcher.defaultShader);
			}
		}
	}

	/**
	 * starts a new path by emptying the list of sub-paths. Call this method when you want to create a new path
	 * @example
	 * // First path
	 * renderer.beginPath();
	 * renderer.setColor("blue");
	 * renderer.moveTo(20, 20);
	 * renderer.lineTo(200, 20);
	 * renderer.stroke();
	 * // Second path
	 * renderer.beginPath();
	 * renderer.setColor("green");
	 * renderer.moveTo(20, 20);
	 * renderer.lineTo(120, 120);
	 * renderer.stroke();
	 */
	beginPath() {
		this.path2D.beginPath();
	}

	/**
	 * begins a new sub-path at the point specified by the given (x, y) coordinates.
	 * @param {number} x - The x axis of the point.
	 * @param {number} y - The y axis of the point.
	 */
	moveTo(x, y) {
		this.path2D.moveTo(x, y);
	}

	/**
	 * adds a straight line to the current sub-path by connecting the sub-path's last point to the specified (x, y) coordinates.
	 */
	lineTo(x, y) {
		this.path2D.lineTo(x, y);
	}

	/**
	 * Adds a quadratic Bezier curve to the current sub-path.
	 * The curve is tessellated into line segments for WebGL rendering.
	 * @param {number} cpx - The x-axis coordinate of the control point.
	 * @param {number} cpy - The y-axis coordinate of the control point.
	 * @param {number} x - The x-axis coordinate of the end point.
	 * @param {number} y - The y-axis coordinate of the end point.
	 */
	quadraticCurveTo(cpx, cpy, x, y) {
		this.path2D.quadraticCurveTo(cpx, cpy, x, y);
	}

	/**
	 * Adds a cubic Bezier curve to the current sub-path.
	 * The curve is tessellated into line segments for WebGL rendering.
	 * @param {number} cp1x - The x-axis coordinate of the first control point.
	 * @param {number} cp1y - The y-axis coordinate of the first control point.
	 * @param {number} cp2x - The x-axis coordinate of the second control point.
	 * @param {number} cp2y - The y-axis coordinate of the second control point.
	 * @param {number} x - The x-axis coordinate of the end point.
	 * @param {number} y - The y-axis coordinate of the end point.
	 */
	bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y) {
		this.path2D.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
	}

	/**
	 * Adds a circular arc to the current sub-path, using the given control points and radius.
	 * The arc is tessellated into line segments for WebGL rendering.
	 * @param {number} x1 - The x-axis coordinate of the first control point.
	 * @param {number} y1 - The y-axis coordinate of the first control point.
	 * @param {number} x2 - The x-axis coordinate of the second control point.
	 * @param {number} y2 - The y-axis coordinate of the second control point.
	 * @param {number} radius - The arc's radius. Must be non-negative.
	 */
	arcTo(x1, y1, x2, y2, radius) {
		this.path2D.arcTo(x1, y1, x2, y2, radius);
	}

	/**
	 * creates a rectangular path whose starting point is at (x, y) and whose size is specified by width and height.
	 * @param {number} x - The x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - The y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 */
	rect(x, y, width, height) {
		this.path2D.rect(x, y, width, height);
	}

	/**
	 * adds a rounded rectangle to the current path.
	 * @param {number} x - The x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - The y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 * @param {number} radii - The corner radius.
	 */
	roundRect(x, y, width, height, radii) {
		this.path2D.roundRect(x, y, width, height, radii);
	}

	/**
	 * stroke the given shape or the current defined path
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse|Bounds} [shape] - a shape object to stroke
	 * @param {boolean} [fill=false] - fill the shape with the current color if true
	 */
	stroke(shape, fill) {
		if (typeof shape === "undefined") {
			this.setBatcher("primitive");
			if (fill === true) {
				// draw all triangles
				this.currentBatcher.drawVertices(
					this.gl.TRIANGLES,
					this.path2D.triangulatePath(),
				);
			} else {
				const dash = this.renderState.lineDash;
				if (dash.length > 0) {
					const pts = this.path2D.points;
					const dashed = dashPath(pts, dash);
					if (dashed.length > 0) {
						this.currentBatcher.drawVertices(this.gl.LINES, dashed);
					}
				} else {
					this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
				}
			}
		} else {
			// dispatches to strokeRect/strokePolygon/etc. which each call setBatcher
			super.stroke(shape, fill);
		}
	}

	/**
	 * fill the given shape or the current defined path
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse|Bounds} [shape] - a shape object to fill
	 */
	fill(shape) {
		this.stroke(shape, true);
	}

	/**
	 * add a straight line from the current point to the start of the current sub-path. If the shape has already been closed or has only one point, this function does nothing
	 */
	closePath() {
		this.path2D.closePath();
	}

	/**
	 * Returns the WebGLContext instance for the renderer
	 * return a reference to the system 2d Context
	 * @returns {WebGLRenderingContext} the current WebGL context
	 */
	/**
	 * The shader program the GL context actually has bound.
	 *
	 * Backed by the CONTEXT rather than by a field on this renderer, because
	 * `GLShader` binds programs too — writing a uniform calls `useProgram`,
	 * and a freshly linked program is left bound — and it is constructed with
	 * a bare `gl`, so it cannot reach a renderer-owned cache. A field here
	 * would silently drift out of step with GL, and every batcher that
	 * compares against it (`syncProgram`, the mesh and primitive paths) would
	 * skip a rebind it actually needed, drawing a pending batch through the
	 * wrong program.
	 * @type {WebGLProgram|undefined}
	 */
	get currentProgram() {
		return this.gl.__meCurrentProgram;
	}

	set currentProgram(value) {
		this.gl.__meCurrentProgram = value;
	}

	getContext() {
		return this.gl;
	}

	/**
	 * set the current blend mode for this renderer. <br>
	 * Both GPU renderers support the full set — the Canvas fallback supports
	 * every mode below except `"none"`: <br>
	 * - "normal" : draws new content on top of the existing content <br>
	 * <img src="../images/normal-blendmode.png" width="180"/> <br>
	 * - "add", "additive", or "lighter" : color values are added together <br>
	 * <img src="../images/add-blendmode.png" width="180"/> <br>
	 * - "multiply" : pixels are multiplied, resulting in a darker picture <br>
	 * <img src="../images/multiply-blendmode.png" width="180"/> <br>
	 * - "screen" : pixels are inverted, multiplied, and inverted again (opposite of multiply) <br>
	 * <img src="../images/screen-blendmode.png" width="180"/> <br>
	 * - "darken" : retains the darkest pixels of both layers <br>
	 * <img src="../images/darken-blendmode.png" width="180"/> <br>
	 * - "lighten" : retains the lightest pixels of both layers <br>
	 * <img src="../images/lighten-blendmode.png" width="180"/> <br>
	 * - "overlay" : multiplies or screens, depending on the backdrop <br>
	 * <img src="../images/overlay-blendmode.png" width="180"/> <br>
	 * - "hard-light" : overlay with the layers swapped — a harsh spotlight <br>
	 * <img src="../images/hard-light-blendmode.png" width="180"/> <br>
	 * - "soft-light" : a diffused spotlight, gentler than hard-light <br>
	 * <img src="../images/soft-light-blendmode.png" width="180"/> <br>
	 * - "color-dodge" : brightens the backdrop to reflect the source <br>
	 * <img src="../images/color-dodge-blendmode.png" width="180"/> <br>
	 * - "color-burn" : darkens the backdrop to reflect the source <br>
	 * <img src="../images/color-burn-blendmode.png" width="180"/> <br>
	 * - "difference" : the absolute difference of the two layers <br>
	 * <img src="../images/difference-blendmode.png" width="180"/> <br>
	 * - "exclusion" : like difference, but lower in contrast <br>
	 * <img src="../images/exclusion-blendmode.png" width="180"/> <br>
	 * - "none" : blending disabled — the source replaces the destination
	 * outright, alpha included <br>
	 * A few draw types cannot honour every mode — 3D meshes, and fills using
	 * a {@link Gradient} — and fall back to "normal" with a one-time console
	 * warning. `setBlendMode` returns what it actually applied, so comparing
	 * the result against your request detects that case.
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
	 * @param {string} [mode="normal"] - blend mode
	 * @param {boolean} [premultipliedAlpha=true] - whether textures use premultiplied alpha (affects the source blend factor)
	 * @returns {string} the blend mode actually applied (may differ if the requested mode is unsupported)
	 */
	setBlendMode(mode = "normal", premultipliedAlpha = true) {
		if (
			this.currentBlendMode !== mode ||
			this.currentPremultipliedAlpha !== premultipliedAlpha
		) {
			const gl = this.gl;
			// geometry already bracketed belongs to the OUTGOING mode —
			// composite it before the mode changes underneath it
			this._drainAdvancedBlend();
			this.flush();
			this.currentBlendMode = mode;
			this.currentPremultipliedAlpha = premultipliedAlpha;

			// One table, shared with the WebGPU backend (`blendStateFor`), so a
			// mode cannot be defined differently on the two GPU paths and make the
			// same scene render differently depending on which one `video.AUTO`
			// picked. Only the translation to `gl.*` enums lives here.
			if (typeof GL_BLEND_OP === "undefined") {
				initBlendEnums(gl);
			}
			const state = blendStateFor(normalizeBlendMode(mode), premultipliedAlpha);
			if (typeof state === "undefined") {
				// "none" — replace: the source overwrites the destination, alpha
				// included, matching the WebGPU backend's absent blend state
				gl.disable(gl.BLEND);
			} else {
				gl.enable(gl.BLEND);
				gl.blendEquation(GL_BLEND_OP[state.operation]);
				gl.blendFunc(
					GL_BLEND_FACTOR[state.srcFactor],
					GL_BLEND_FACTOR[state.dstFactor],
				);
			}

			// An unimplemented mode reports — and remembers — "normal". The six
			// advanced modes are NOT unimplemented: their geometry rasterizes
			// under the source-over just installed, and the token has to survive
			// because `setBatcher` reads it back to open the compositing bracket
			// and `BlendEffect` reads it to pick the formula.
			if (isSupportedBlendMode(mode) === false) {
				this.currentBlendMode = "normal";
			}
		}
		return this.currentBlendMode;
	}

	/**
	 * restores the most recently saved renderer state by popping the top entry in the drawing state stack
	 * @example
	 * // Save the current state
	 * renderer.save();
	 *
	 * // apply a transform and draw a rect
	 * renderer.tranform(matrix);
	 * renderer.fillRect(10, 10, 100, 100);
	 *
	 * // Restore to the state saved by the most recent call to save()
	 * renderer.restore();
	 */
	restore() {
		const canvas = this.getCanvas();
		// Peek at the to-be-restored scissor (without mutating state)
		// so we can detect whether the restore actually changes the
		// scissor box. If it does, drain pending vertices first under
		// the *current* GL scissor — otherwise vertices queued inside
		// a tighter clip would flush later under a more permissive
		// scissor and visually escape their clip.
		// `peekScissor` returns the saved box directly (live ref) or
		// null when scissor will be inactive after the restore. Treat
		// the returned array as read-only — it points into the stack.
		const peek = this.renderState.peekScissor();
		const cur = this.currentScissor;
		const curActive = this._scissorActive === true;
		const willBeActive = peek !== null;
		const scissorChanging =
			curActive !== willBeActive ||
			(willBeActive &&
				(cur[0] !== peek[0] ||
					cur[1] !== peek[1] ||
					cur[2] !== peek[2] ||
					cur[3] !== peek[3]));
		if (scissorChanging) {
			this.flush();
		}
		const result = this.renderState.restore(canvas.width, canvas.height);
		if (result !== null) {
			this.setBlendMode(result.blendMode);
			if (scissorChanging) {
				const gl = this.gl;
				if (result.scissorActive) {
					const next = this.currentScissor;
					gl.enable(gl.SCISSOR_TEST);
					this._scissorActive = true;
					// `currentScissor` stores screen-space coords directly
					// (see `clipRect`); apply with the GL bottom-left y-flip.
					gl.scissor(
						next[0],
						canvas.height - next[3] - next[1],
						next[2],
						next[3],
					);
				} else {
					gl.disable(gl.SCISSOR_TEST);
					this._scissorActive = false;
				}
			}
		}
		// sync gradient and shader from renderState
		this._currentGradient = this.renderState.currentGradient;
		this.customShader = this.renderState.currentShader;
	}

	/**
	 * saves the entire state of the renderer by pushing the current state onto a stack.
	 * @example
	 * // Save the current state
	 * renderer.save();
	 *
	 * // apply a transform and draw a rect
	 * renderer.tranform(matrix);
	 * renderer.fillRect(10, 10, 100, 100);
	 *
	 * // Restore to the state saved by the most recent call to save()
	 * renderer.restore();
	 */
	save() {
		this.renderState.currentShader = this.customShader;
		this.renderState.save(this._scissorActive === true);
	}

	/**
	 * adds a rotation to the transformation matrix.
	 * @param {number} angle - the rotation angle, clockwise in radians
	 * @example
	 * // Rotated rectangle
	 * renderer.rotate((45 * Math.PI) / 180);
	 * renderer.setColor("red");
	 * renderer.fillRect(10, 10, 100, 100);
	 *
	 * // Reset transformation matrix to the identity matrix
	 * renderer.setTransform(1, 0, 0, 1, 0, 0);
	 */
	rotate(angle) {
		this.currentTransform.rotate(angle);
	}

	/**
	 * adds a scaling transformation to the renderer units horizontally and/or vertically
	 * @param {number} x - Scaling factor in the horizontal direction. A negative value flips pixels across the vertical axis. A value of 1 results in no horizontal scaling.
	 * @param {number} y - Scaling factor in the vertical direction. A negative value flips pixels across the horizontal axis. A value of 1 results in no vertical scaling
	 */
	scale(x, y) {
		this.currentTransform.scale(x, y, 1);
	}

	/**
	 * Map the backend-neutral default filter mode
	 * ({@link Renderer#getDefaultTextureFilter}) to the GL enum
	 * (`gl.LINEAR` / `gl.NEAREST`) used for textures that don't carry their own
	 * `texture.filter`. The neutral resolver lives on the base renderer so a
	 * future WebGPU backend reuses it and maps to `GPUFilterMode` instead.
	 * @returns {number} `gl.LINEAR` or `gl.NEAREST`
	 * @ignore
	 * @internal
	 */
	_glTextureFilter() {
		return this.getDefaultTextureFilter() === "linear"
			? this.gl.LINEAR
			: this.gl.NEAREST;
	}

	/**
	 * Re-apply a min/mag filter to every currently-bound texture across all
	 * batchers (see https://github.com/melonjs/melonJS/issues/1279). Shared by
	 * {@link WebGLRenderer#setAntiAlias} and {@link WebGLRenderer#setTextureFilter}.
	 * @param {number} filter - `gl.LINEAR` or `gl.NEAREST`
	 * @ignore
	 * @internal
	 */
	_reapplyTextureFilter(filter) {
		const gl = this.gl;
		this.batchers.forEach((batcher) => {
			if (batcher.boundTextures) {
				for (let i = 0; i < batcher.boundTextures.length; i++) {
					if (typeof batcher.boundTextures[i] !== "undefined") {
						gl.activeTexture(gl.TEXTURE0 + i);
						gl.bindTexture(gl.TEXTURE_2D, batcher.boundTextures[i]);
						gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
						gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
					}
				}
				// reset so next bindTexture2D re-selects the correct unit
				batcher.currentTextureUnit = -1;
			}
		});
	}

	/**
	 * enable/disable image smoothing (scaling interpolation)
	 * @param {boolean} [enable=false]
	 */
	setAntiAlias(enable = false) {
		if (this.settings.antiAlias !== enable) {
			super.setAntiAlias(enable);
			// re-filter bound textures to the resolved default — honors an explicit
			// `textureFilter` (so toggling antiAlias won't override a "nearest" /
			// "linear" choice), and tracks antiAlias when `textureFilter` is "auto".
			this._reapplyTextureFilter(this._glTextureFilter());
		}
	}

	/**
	 * Set the default texture magnification/minification filter at runtime,
	 * decoupled from {@link WebGLRenderer#setAntiAlias} (which controls MSAA).
	 * Records the setting (via the base) then re-filters every currently-bound
	 * texture. WebGL only.
	 * @param {"auto"|"nearest"|"linear"} [mode="auto"] - `"auto"` follows `antiAlias`
	 * @see Renderer#getDefaultTextureFilter
	 */
	setTextureFilter(mode = "auto") {
		super.setTextureFilter(mode);
		this._reapplyTextureFilter(this._glTextureFilter());
	}

	/**
	 * Set the global alpha
	 * @param {number} alpha - 0.0 to 1.0 values accepted.
	 */
	setGlobalAlpha(alpha) {
		this.currentColor.alpha = alpha;
	}

	/**
	 * Return the global alpha
	 * @returns {number} global alpha value
	 */
	getGlobalAlpha() {
		return this.currentColor.alpha;
	}

	/**
	 * Set the current fill & stroke style color.
	 * By default, or upon reset, the value is set to #000000.
	 * @param {Color|string|Gradient} color - css color string or a Gradient object.
	 */
	setColor(color) {
		if (color instanceof Gradient) {
			this.renderState.currentGradient = color;
			this._currentGradient = color;
		} else {
			this.renderState.currentGradient = null;
			this._currentGradient = null;
			const alpha = this.currentColor.alpha;
			this.currentColor.copy(color);
			this.currentColor.alpha *= alpha;
		}
	}

	/**
	 * Stroke an arc at the specified coordinates with given radius, start and end points
	 * @param {number} x - arc center point x-axis
	 * @param {number} y - arc center point y-axis
	 * @param {number} radius - arc radius
	 * @param {number} start - start angle in radians
	 * @param {number} end - end angle in radians
	 * @param {boolean} [antiClockwise=false] - draw arc anti-clockwise
	 * @param {boolean} [fill=false] - also fill the shape with the current color if true
	 */
	strokeArc(x, y, radius, start, end, antiClockwise = false, fill = false) {
		if (fill === true) {
			this.fillArc(x, y, radius, start, end, antiClockwise);
			return;
		}
		this.setBatcher("primitive");
		this.path2D.beginPath();
		this.path2D.arc(x, y, radius, start, end, antiClockwise);
		this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
	}

	/**
	 * Fill an arc at the specified coordinates with given radius, start and end points
	 * @param {number} x - arc center point x-axis
	 * @param {number} y - arc center point y-axis
	 * @param {number} radius - arc radius
	 * @param {number} start - start angle in radians
	 * @param {number} end - end angle in radians
	 * @param {boolean} [antiClockwise=false] - draw arc anti-clockwise
	 */
	fillArc(x, y, radius, start, end, antiClockwise = false) {
		if (this._currentGradient) {
			this.#gradientMask(
				() => {
					this.fillArc(x, y, radius, start, end, antiClockwise);
				},
				x - radius,
				y - radius,
				radius * 2,
				radius * 2,
			);
			return;
		}
		this.setBatcher("primitive");
		let diff = Math.abs(end - start);
		if (antiClockwise) {
			diff = Math.PI * 2 - diff;
		}
		const segments = Math.max(
			4,
			Math.round((diff * radius) / this.path2D.arcResolution),
		);
		const startAngle = antiClockwise ? end : start;
		this.currentBatcher.drawVertices(
			this.gl.TRIANGLES,
			generateTriangleFan(
				x,
				y,
				radius,
				radius,
				startAngle,
				startAngle + diff,
				segments,
			),
		);
	}

	/**
	 * Stroke an ellipse at the specified coordinates with given radius
	 * @param {number} x - ellipse center point x-axis
	 * @param {number} y - ellipse center point y-axis
	 * @param {number} w - horizontal radius of the ellipse
	 * @param {number} h - vertical radius of the ellipse
	 * @param {boolean} [fill=false] - also fill the shape with the current color if true
	 */
	strokeEllipse(x, y, w, h, fill = false) {
		if (fill === true) {
			this.fillEllipse(x, y, w, h);
			return;
		}
		this.setBatcher("primitive");
		this.path2D.beginPath();
		this.path2D.ellipse(x, y, w, h, 0, 0, 360);
		this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
	}

	/**
	 * Fill an ellipse at the specified coordinates with given radius
	 * @param {number} x - ellipse center point x-axis
	 * @param {number} y - ellipse center point y-axis
	 * @param {number} w - horizontal radius of the ellipse
	 * @param {number} h - vertical radius of the ellipse
	 */
	fillEllipse(x, y, w, h) {
		if (this._currentGradient) {
			this.#gradientMask(
				() => {
					this.fillEllipse(x, y, w, h);
				},
				x - w,
				y - h,
				w * 2,
				h * 2,
			);
			return;
		}
		this.setBatcher("primitive");
		const segments = Math.max(
			8,
			Math.round((Math.PI * (w + h)) / this.path2D.arcResolution),
		);
		this.currentBatcher.drawVertices(
			this.gl.TRIANGLES,
			generateTriangleFan(x, y, w, h, 0, Math.PI * 2, segments),
		);
	}

	/**
	 * Stroke a line of the given two points
	 * @param {number} startX - the start x coordinate
	 * @param {number} startY - the start y coordinate
	 * @param {number} endX - the end x coordinate
	 * @param {number} endY - the end y coordinate
	 */
	strokeLine(startX, startY, endX, endY) {
		this.setBatcher("primitive");
		const dash = this.renderState.lineDash;
		if (dash.length > 0) {
			const segments = dashSegments(startX, startY, endX, endY, dash);
			if (segments.length > 0) {
				this.currentBatcher.drawVertices(this.gl.LINES, segments);
			}
		} else {
			this.path2D.beginPath();
			this.path2D.moveTo(startX, startY);
			this.path2D.lineTo(endX, endY);
			this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
		}
	}

	/**
	 * Fill a line of the given two points
	 * @param {number} startX - the start x coordinate
	 * @param {number} startY - the start y coordinate
	 * @param {number} endX - the end x coordinate
	 * @param {number} endY - the end y coordinate
	 */
	fillLine(startX, startY, endX, endY) {
		this.strokeLine(startX, startY, endX, endY);
	}

	/**
	 * Stroke a Polygon on the screen with a specified color
	 * @param {Polygon} poly - the shape to draw
	 * @param {boolean} [fill=false] - also fill the shape with the current color if true
	 */
	strokePolygon(poly, fill = false) {
		if (fill === true) {
			this.fillPolygon(poly);
			return;
		}
		const points = poly.points;
		const len = points.length;

		this.translate(poly.pos.x, poly.pos.y);

		this.setBatcher("primitive");
		this.path2D.beginPath();
		for (let i = 0; i < len - 1; i++) {
			const curPoint = points[i];
			const nextPoint = points[i + 1];
			this.path2D.moveTo(curPoint.x, curPoint.y);
			this.path2D.lineTo(nextPoint.x, nextPoint.y);
		}
		this.path2D.closePath();
		const dash = this.renderState.lineDash;
		if (dash.length > 0) {
			const pts = this.path2D.points;
			const dashed = dashPath(pts, dash);
			if (dashed.length > 0) {
				this.currentBatcher.drawVertices(this.gl.LINES, dashed);
			}
		} else {
			this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
		}
		// add round joins at vertices for thick lines
		if (this.lineWidth > 1) {
			const radius = this.lineWidth / 2;
			const joinPoints = [];
			for (let i = 1; i < len; i++) {
				joinPoints.push(points[i]);
			}
			const lastPoint = points[len - 1];
			const firstPoint = points[0];
			if (!lastPoint.equals(firstPoint)) {
				joinPoints.push(firstPoint);
			}
			this.currentBatcher.drawVertices(
				this.gl.TRIANGLES,
				generateJoinCircles(joinPoints, radius),
			);
		}

		this.translate(-poly.pos.x, -poly.pos.y);
	}

	/**
	 * Fill a me.Polygon on the screen
	 * @param {Polygon} poly - the shape to draw
	 */
	fillPolygon(poly) {
		if (this._currentGradient) {
			const bounds = poly.getBounds();
			// translate to polygon's local space so gradient coords match
			this.translate(poly.pos.x, poly.pos.y);
			this.#gradientMask(
				() => {
					// draw polygon vertices directly (already translated)
					this.setBatcher("primitive");
					const indices = poly.getIndices();
					const points = poly.points;
					const verts = this._polyVerts;
					const len = indices.length;
					while (verts.length < len) {
						verts.push({ x: 0, y: 0 });
					}
					for (let i = 0; i < len; i++) {
						const src = points[indices[i]];
						verts[i].x = src.x;
						verts[i].y = src.y;
					}
					this.currentBatcher.drawVertices(this.gl.TRIANGLES, verts, len);
				},
				// use local bounds (subtract pos since getBounds includes it)
				bounds.x - poly.pos.x,
				bounds.y - poly.pos.y,
				bounds.width,
				bounds.height,
			);
			this.translate(-poly.pos.x, -poly.pos.y);
			return;
		}
		this.setBatcher("primitive");
		this.translate(poly.pos.x, poly.pos.y);
		const indices = poly.getIndices();
		const points = poly.points;
		const verts = this._polyVerts;
		const len = indices.length;

		// grow the scratch array if needed
		while (verts.length < len) {
			verts.push({ x: 0, y: 0 });
		}

		// copy point coordinates so drawVertices won't mutate the polygon
		for (let i = 0; i < len; i++) {
			const src = points[indices[i]];
			verts[i].x = src.x;
			verts[i].y = src.y;
		}

		this.currentBatcher.drawVertices(this.gl.TRIANGLES, verts, len);
		this.translate(-poly.pos.x, -poly.pos.y);
	}

	/**
	 * Draw a stroke rectangle at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 * @param {boolean} [fill=false] - also fill the shape with the current color if true
	 */
	strokeRect(x, y, width, height, fill = false) {
		if (fill === true) {
			this.fillRect(x, y, width, height);
			return;
		}
		this.setBatcher("primitive");
		this.path2D.beginPath();
		this.path2D.rect(x, y, width, height);
		this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
		// add round joins at corners for thick lines
		if (this.lineWidth > 1) {
			const radius = this.lineWidth / 2;
			this.currentBatcher.drawVertices(
				this.gl.TRIANGLES,
				generateJoinCircles(
					[
						{ x, y },
						{ x: x + width, y },
						{ x: x + width, y: y + height },
						{ x, y: y + height },
					],
					radius,
				),
			);
		}
	}

	/**
	 * Draw a filled rectangle at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 */
	fillRect(x, y, width, height) {
		if (this._currentGradient) {
			// toCanvas() calls invalidate() which flushes pending draws.
			// The bake is fixed-resolution: the returned width/height are the
			// SOURCE rect inside the shared canvas (scaled-down for large
			// rects), stretched back by the destination quad.
			const baked = this._currentGradient.toCanvas(this, x, y, width, height);
			this.drawImage(
				baked.canvas,
				0,
				0,
				baked.width,
				baked.height,
				x,
				y,
				width,
				height,
			);
			return;
		}
		this.setBatcher("primitive");
		// 2 triangles directly — avoids path2D + earcut overhead
		const right = x + width;
		const bottom = y + height;
		const pts = this._rectTriangles;
		pts[0].x = x;
		pts[0].y = y;
		pts[1].x = right;
		pts[1].y = y;
		pts[2].x = x;
		pts[2].y = bottom;
		pts[3].x = right;
		pts[3].y = y;
		pts[4].x = right;
		pts[4].y = bottom;
		pts[5].x = x;
		pts[5].y = bottom;
		this.currentBatcher.drawVertices(this.gl.TRIANGLES, pts);
	}

	/**
	 * Stroke a rounded rectangle at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the rounded rectangle starting point.
	 * @param {number} y - y axis of the coordinate for the rounded rectangle starting point.
	 * @param {number} width - The rounded rectangle's width.
	 * @param {number} height - The rounded rectangle's height.
	 * @param {number} radius - The rounded corner's radius.
	 * @param {boolean} [fill=false] - also fill the shape with the current color if true
	 */
	strokeRoundRect(x, y, width, height, radius, fill = false) {
		if (fill === true) {
			this.fillRoundRect(x, y, width, height, radius);
			return;
		}
		this.setBatcher("primitive");
		this.path2D.beginPath();
		this.path2D.roundRect(x, y, width, height, radius);
		this.currentBatcher.drawVertices(this.gl.LINES, this.path2D.points);
	}

	/**
	 * Draw a rounded filled rectangle at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the rounded rectangle starting point.
	 * @param {number} y - y axis of the coordinate for the rounded rectangle starting point.
	 * @param {number} width - The rounded rectangle's width.
	 * @param {number} height - The rounded rectangle's height.
	 * @param {number} radius - The rounded corner's radius.
	 */
	fillRoundRect(x, y, width, height, radius) {
		if (this._currentGradient) {
			this.#gradientMask(
				() => {
					this.fillRoundRect(x, y, width, height, radius);
				},
				x,
				y,
				width,
				height,
			);
			return;
		}
		this.setBatcher("primitive");
		const r = Math.min(radius, width / 2, height / 2);
		const verts = [];

		// inner cross: 3 rects (6 triangles = 18 vertices)
		// horizontal bar (full width, inner height)
		verts.push(
			{ x, y: y + r },
			{ x: x + width, y: y + r },
			{ x, y: y + height - r },
			{ x: x + width, y: y + r },
			{ x: x + width, y: y + height - r },
			{ x, y: y + height - r },
		);
		// top bar (inner width, radius height)
		verts.push(
			{ x: x + r, y },
			{ x: x + width - r, y },
			{ x: x + r, y: y + r },
			{ x: x + width - r, y },
			{ x: x + width - r, y: y + r },
			{ x: x + r, y: y + r },
		);
		// bottom bar (inner width, radius height)
		verts.push(
			{ x: x + r, y: y + height - r },
			{ x: x + width - r, y: y + height - r },
			{ x: x + r, y: y + height },
			{ x: x + width - r, y: y + height - r },
			{ x: x + width - r, y: y + height },
			{ x: x + r, y: y + height },
		);

		// 4 corner arcs as triangle fans
		const cornerSegments = Math.max(
			4,
			Math.round((Math.PI * r) / 2 / this.path2D.arcResolution),
		);
		const PI = Math.PI;
		verts.push(
			...generateTriangleFan(x + r, y + r, r, r, PI, PI * 1.5, cornerSegments),
		); // top-left
		verts.push(
			...generateTriangleFan(
				x + width - r,
				y + r,
				r,
				r,
				PI * 1.5,
				PI * 2,
				cornerSegments,
			),
		); // top-right
		verts.push(
			...generateTriangleFan(
				x + width - r,
				y + height - r,
				r,
				r,
				0,
				PI * 0.5,
				cornerSegments,
			),
		); // bottom-right
		verts.push(
			...generateTriangleFan(
				x + r,
				y + height - r,
				r,
				r,
				PI * 0.5,
				PI,
				cornerSegments,
			),
		); // bottom-left

		this.currentBatcher.drawVertices(this.gl.TRIANGLES, verts);
	}

	/**
	 * Stroke a Point at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the point.
	 * @param {number} y - y axis of the coordinate for the point.
	 */
	strokePoint(x, y) {
		this.strokeLine(x, y, x + 1, y + 1);
	}

	/**
	 * Draw a point at the specified coordinates
	 * @param {number} x - x axis of the coordinate for the point.
	 * @param {number} y - y axis of the coordinate for the point.
	 */
	fillPoint(x, y) {
		this.strokePoint(x, y);
	}

	/**
	 * Draw a gradient-filled shape by masking with the shape and filling the bounding rect.
	 * Temporarily disables the gradient to prevent recursion in the fill methods.
	 * @param {Function} drawShape - draws the shape into the stencil buffer
	 * @param {number} x - bounding rect x
	 * @param {number} y - bounding rect y
	 * @param {number} w - bounding rect width
	 * @param {number} h - bounding rect height
	 * @ignore
	 * @internal
	 */
	#gradientMask(drawShape, x, y, w, h) {
		const gl = this.gl;
		const grad = this._currentGradient;
		const hasMask = this.maskLevel > 0;
		this._currentGradient = null;

		// A gradient fill drives the stencil itself — marking, clipping, then
		// clearing its marker — and re-enters the fill methods twice to do it.
		// Bracketing any of that for an advanced blend mode would corrupt the
		// mask level, so the whole span is locked out of the bracket and the
		// fill falls back to source-over. Loudly: a silent, data-dependent
		// fallback is precisely what advanced blend support exists to remove.
		const bracketed = isAdvancedBlendMode(this.currentBlendMode);
		if (bracketed === true) {
			this._warnAdvancedBlendFallback("gradient fills");
			this._drainAdvancedBlend();
		}
		this._advancedBlendBusy++;

		this.flush();

		gl.enable(gl.STENCIL_TEST);
		gl.colorMask(false, false, false, false);

		// The stencil value of currently-VISIBLE pixels, as installed by
		// setMask (its single source of truth). The shape's visible pixels
		// are tagged with a high-bit marker (mask levels only use the low 7
		// bits, so the marker can never collide with one), the gradient is
		// clipped to the marker, then the marker is cleared and setMask's
		// exact render test is re-installed.
		const visibleRef = this._maskVisibleRef;
		let markRef = 1;

		if (hasMask) {
			markRef = 0x80 | visibleRef;
			// tag: only pixels that are visible under the active mask
			// (low bits == visibleRef) get the marker written
			gl.stencilFunc(gl.EQUAL, markRef, 0x7f);
			gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
		} else {
			gl.clear(gl.STENCIL_BUFFER_BIT);
			gl.stencilFunc(gl.ALWAYS, 1, 0xff);
			gl.stencilOp(gl.REPLACE, gl.REPLACE, gl.REPLACE);
		}

		drawShape();
		this.flush();

		// use stencil to clip the gradient to the marked pixels
		gl.colorMask(true, true, true, true);
		gl.stencilFunc(gl.EQUAL, markRef, 0xff);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);

		this._currentGradient = grad;
		this.fillRect(x, y, w, h);
		this.flush();

		if (hasMask) {
			// clear the marker: write the visible value back on the shape's
			// pixels (REPLACE writes the func ref — a no-op on unmarked
			// visible pixels, and it strips the high bit from marked ones)
			this._currentGradient = null;
			gl.colorMask(false, false, false, false);
			gl.stencilFunc(gl.EQUAL, visibleRef, 0x7f);
			gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
			drawShape();
			this.flush();
			gl.colorMask(true, true, true, true);
			// re-install the exact render test setMask had established
			gl.stencilFunc(gl.EQUAL, visibleRef, 0xff);
			gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
			this._currentGradient = grad;
		} else {
			gl.disable(gl.STENCIL_TEST);
		}
		this._advancedBlendBusy--;
	}

	/**
	 * Reset (overrides) the renderer transformation matrix to the
	 * identity one, and then apply the given transformation matrix.
	 * @param {Matrix2d|Matrix3d|number} a - a matrix to transform by, or the a component to multiply the current matrix by
	 * @param {number} [b] - the b component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [c] - the c component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [d] - the d component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [e] - the e component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [f] - the f component to multiply the current matrix by; omitted when `a` is a matrix
	 */
	setTransform(a, b, c, d, e, f) {
		this.resetTransform();
		this.transform(a, b, c, d, e, f);
	}

	/**
	 * Multiply given matrix into the renderer tranformation matrix
	 * @see {@link WebGLRenderer.setTransform} which will reset the current transform matrix prior to performing the new transformation
	 * @param {Matrix2d|Matrix3d|number} a - a matrix to transform by, or the a component to multiply the current matrix by
	 * @param {number} [b] - the b component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [c] - the c component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [d] - the d component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [e] - the e component to multiply the current matrix by; omitted when `a` is a matrix
	 * @param {number} [f] - the f component to multiply the current matrix by; omitted when `a` is a matrix
	 */
	transform(a, b, c, d, e, f) {
		if (typeof a === "object") {
			// accepts both Matrix2d and Matrix3d (no temporary copy needed)
			this.currentTransform.multiply(a);
		} else {
			// individual 2D affine components
			this.currentTransform.multiply(
				_tempMatrix.setTransform(
					a,
					b,
					0,
					0,
					c,
					d,
					0,
					0,
					0,
					0,
					1,
					0,
					e,
					f,
					0,
					1,
				),
			);
		}
		if (this.settings.subPixel === false) {
			// snap position values to pixel grid
			const a = this.currentTransform.val;
			a[12] |= 0;
			a[13] |= 0;
		}
	}

	/**
	 * adds a translation transformation to the current matrix.
	 * @param {number} x - Distance to move in the horizontal direction. Positive values are to the right, and negative to the left.
	 * @param {number} y - Distance to move in the vertical direction. Positive values are down, and negative are up.
	 */
	translate(x, y) {
		this.currentTransform.translate(x, y);
		if (this.settings.subPixel === false) {
			// snap position values to pixel grid
			const a = this.currentTransform.val;
			a[12] |= 0;
			a[13] |= 0;
		}
	}

	/**
	 * clip the given region from the original canvas. Once a region is clipped,
	 * all future drawing will be limited to the clipped region.
	 * You can however save the current region using the save(),
	 * and restore it (with the restore() method) any time in the future.
	 * (<u>this is an experimental feature !</u>)
	 * @param {number} x - x axis of the coordinate for the upper-left corner of the rectangle to start clipping from.
	 * @param {number} y - y axis of the coordinate for the upper-left corner of the rectangle to start clipping from.
	 * @param {number} width - the width of the rectangle to start clipping from.
	 * @param {number} height - the height of the rectangle to start clipping from.
	 */
	clipRect(x, y, width, height) {
		const canvas = this.getCanvas();
		const gl = this.gl;

		// `ColorLayer`-style hosts have `Infinity` dimensions + a 0.5
		// anchor, leaving `currentTransform.tx/ty = -Infinity`. Running
		// transform math on that produces NaN corners and a broken
		// `gl.scissor`. Treat a non-finite transform as "no clip".
		const m = this.currentTransform;
		if (!Number.isFinite(m.tx) || !Number.isFinite(m.ty)) {
			if (this._scissorActive) {
				// drain pending vertices under the active scissor first;
				// otherwise they would flush later under no scissor.
				this.flush();
				gl.disable(gl.SCISSOR_TEST);
				this._scissorActive = false;
			}
			return;
		}

		// derive the screen-space AABB by feeding the rect's 4 corners
		// through `currentTransform` via `Bounds.addFrame` (which short-
		// circuits the corner walk when the transform is identity).
		// `gl.scissor` is not transform-aware, so any rotation collapses
		// to the rotated-rect AABB on screen — Canvas's `context.clip()`
		// would produce a true polygonal clip, but downstream rendering
		// only observes the AABB anyway. Issue #1349.
		const aabb = this._clipAABB;
		aabb.clear();
		aabb.addFrame(x, y, x + width, y + height, m);
		const sx = Math.floor(aabb.min.x);
		const sy = Math.floor(aabb.min.y);
		const sw = Math.ceil(aabb.max.x - sx);
		const sh = Math.ceil(aabb.max.y - sy);

		// If the resulting screen AABB covers the whole canvas, treat as
		// no-clip and disable the scissor. Caller intent: "clip to the
		// full viewport" → no scissor needed. Works regardless of the
		// transform that produced the AABB.
		if (
			sx <= 0 &&
			sy <= 0 &&
			sx + sw >= canvas.width &&
			sy + sh >= canvas.height
		) {
			if (this._scissorActive) {
				this.flush();
				gl.disable(gl.SCISSOR_TEST);
				this._scissorActive = false;
			}
			return;
		}

		// `currentScissor` now stores screen-space coords directly so
		// `restore()` can re-apply without re-running the transform math.
		const cs = this.currentScissor;
		if (
			this._scissorActive &&
			cs[0] === sx &&
			cs[1] === sy &&
			cs[2] === sw &&
			cs[3] === sh
		) {
			return; // already at the right scissor
		}
		// flush the batcher BEFORE enabling/changing scissor — pending
		// vertices were queued under the previous clip state and need to
		// drain there.
		this.flush();
		gl.enable(gl.SCISSOR_TEST);
		this._scissorActive = true;
		// GL scissor uses bottom-left origin
		gl.scissor(sx, canvas.height - sh - sy, sw, sh);
		cs[0] = sx;
		cs[1] = sy;
		cs[2] = sw;
		cs[3] = sh;
	}

	/**
	 * A mask limits rendering elements to the shape and position of the given mask object.
	 * If the drawing or rendering area is larger than the mask, only the intersecting part of the renderable will be visible.
	 * (Note Mask are not preserved through renderer context save and restore and need so be manually cleared)
	 * @see CanvasRenderer#clearMask
	 * @param {Rect|RoundRect|Polygon|Line|Ellipse} [mask] - a shape defining the mask to be applied
	 * @param {boolean} [invert=false] - either the given shape should define what is visible (default) or the opposite
	 */
	setMask(mask, invert = false) {
		const gl = this.gl;

		// flush the batcher
		this.flush();

		if (this.maskLevel === 0) {
			// Enable and setup GL state to write to stencil buffer
			gl.enable(gl.STENCIL_TEST);
			gl.clear(gl.STENCIL_BUFFER_BIT);
		}

		this.maskLevel++;
		if (this.maskLevel > 0x7f) {
			// mask levels live in the stencil's low 7 bits — the high bit is
			// reserved as #gradientMask's temporary marker, and an 8-bit
			// stencil couldn't represent deeper nesting anyway
			this.maskLevel = 0x7f;
			if (this._maskDepthWarned !== true) {
				/**
				 * @ignore
				 * @internal
				 */
				this._maskDepthWarned = true;
				console.warn(
					"melonJS: setMask nesting deeper than 127 — mask level clamped",
				);
			}
		}

		// Write phase: increment stencil for the drawn shape's pixels so each
		// setMask call adds +1 to stencil inside the shape. This lets chained
		// inverted masks (e.g. multi-light cutouts) accumulate correctly:
		// stencil > 0 means "covered by some mask shape"; stencil == 0 means
		// "outside every mask shape".
		gl.colorMask(false, false, false, false);
		gl.stencilMask(0xff);
		gl.stencilFunc(gl.ALWAYS, 0, 0xff);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.INCR);

		// fill the given mask shape
		this.fill(mask);

		// flush the batcher
		this.flush();

		gl.colorMask(true, true, true, true);

		// Render phase:
		// - invert=true: render where no mask shape covers (stencil == 0).
		//   With chained invert calls this naturally produces canvas \ ⋃ shapes.
		// - invert=false: render where the just-added mask covers
		//   (stencil == maskLevel, i.e. the intersection of all masks so far).
		if (invert === true) {
			gl.stencilFunc(gl.EQUAL, 0, 0xff);
		} else {
			gl.stencilFunc(gl.EQUAL, this.maskLevel, 0xff);
		}
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
		this._maskVisibleRef = invert === true ? 0 : this.maskLevel;
	}

	/**
	 * disable (remove) the rendering mask set through setMask.
	 * @see WebGLRenderer#setMask
	 */
	clearMask() {
		if (this.maskLevel > 0) {
			// flush the batcher
			this.flush();
			this.maskLevel = 0;
			this._maskVisibleRef = 0;
			this.gl.disable(this.gl.STENCIL_TEST);
		}
	}
}
